import { useEffect, useMemo, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend,
} from 'recharts';
import {
  fetchZoneBriefing,
  fetchCarrierRoutesGeoJSON,
  fetchFiberGeoJSON,
  fetchFiberRowPipelinesGeoJSON,
  fetchIxpFacilitiesGeoJSON,
  fetchPipelinesGeoJSON,
  fetchPowerGeoJSON,
  fetchElectricTerritoriesGeoJSON,
  fetchOsmSwitchyardsGeoJSON,
  fetchFederalLandsGeoJSON,
  fetchFloodZonesGeoJSON,
  fetchCriticalHabitatGeoJSON,
  fetchMineSubsidenceGeoJSON,
  fetchAmlSitesGeoJSON,
  fetchWetlandsGeoJSON,
  fetchAquifersGeoJSON,
  fetchBrackishZonesGeoJSON,
  fetchWaterRegulatoryZonesGeoJSON,
  fetchTwdbWellsGeoJSON,
  fetchHighwaysGeoJSON,
  fetchRailroadsGeoJSON,
  fetchAirportsGeoJSON,
  type ZoneBriefing as ZoneBriefingData,
  type ZoneEligibleParcel,
  type ZoneOwnershipGroup,
  type ZoneSpreadCard,
} from '../../lib/api';
import { REPORT, TierEyebrow } from '../print/PrintAtoms';
import { createZoneShareToken, type CreateZoneShareTokenResponse } from '../../lib/admin-api';
import './zone-briefing.css';

const MW_PER_MMCF = 5.07;
const FIRST_DELIVERABLE_MW = 250;
const FIRST_DELIVERABLE_MMCF = FIRST_DELIVERABLE_MW / MW_PER_MMCF;

mapboxgl.accessToken = (import.meta.env.VITE_MAPBOX_TOKEN as string) || '';

const SERIF = 'Georgia, "Iowan Old Style", "Times New Roman", serif';
const SANS = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

// Phase colors mirror the main-map operator-AOI layer paint
// (src/components/MapView.tsx#operator-aois-phase-fill) so the zone
// briefing reads visually consistent with the main map when an Integra
// reviewer flips between the two. Phase 2 = deep amber-brown (NE),
// Phase 3 = deep blue (SW). Crown Pine coral is hue-distinct from both,
// so the parcel tint still reads uniform across phase boundaries.
const PHASE_COLOR: Record<string, string> = {
  phase_2: '#b45309',  // deep amber-brown (operator-AOI Phase 2)
  phase_3: '#1d4ed8',  // deep blue (operator-AOI Phase 3)
  aoi_outer: REPORT.tertiary,
};

// Owner-tint palette, Crown Pine 1 is the ONLY warm tone so the headline
// counterparty stands out on the map. All other slots stay in cool/neutral
// REPORT tokens. Federal moved off REPORT.fail (red) onto a cool dark for
// the same reason, only Crown Pine 1 should read as red/coral/amber.
// Per feedback_stay_inside_palette.md, NO new hex; everything sourced from REPORT.
const OWNER_PALETTE = [
  REPORT.accent,      // #1, sunrise / coral (Crown Pine 1, ONLY warm tone)
  REPORT.highlight,   // #2, mid-ridge cool blue
  REPORT.pass,        // #3, olive
  REPORT.body,        // #4, dark-ridge
  REPORT.sand,        // #5, cool gray
  REPORT.secondary,   // #6, far-ridge gray-blue
];
const FEDERAL_COLOR = REPORT.heading;        // near-ridge dark cool (was REPORT.fail / red)
const OTHER_PRIVATE_COLOR = REPORT.tertiary;
const UNKNOWN_OWNER_COLOR = REPORT.rule as string;

type LayerKey = 'ownership' | 'power' | 'land' | 'fiber' | 'water' | 'infra';

const LAYER_LABEL: Record<LayerKey, string> = {
  ownership: 'Ownership',
  power: 'Power',
  land: 'Land',
  fiber: 'Fiber',
  water: 'Water',
  infra: 'Logistics',
};

function layerHint(active: LayerKey, data: ZoneBriefingData, live?: LiveCounts, fiberStats?: { minMi: number; maxMi: number; maxProviders: number } | null): string {
  const takeaway = data.takeawayPipelines.length;
  switch (active) {
    case 'ownership': {
      const rollup = data.layers.ownershipRollup;
      if (rollup.length < 2) return '';
      const top = rollup[0];
      const nextThree = rollup.slice(1, 4).reduce((s, g) => s + g.total_acres, 0);
      if (top.total_acres > nextThree) {
        return `${top.owner_label} holds more acreage than the next three entities combined; single-counterparty deal.`;
      }
      return `${rollup.length} ownership entities. ${top.owner_label} is the largest holder.`;
    }
    case 'power': {
      const p = live?.power;
      if (!p || p.subsCount == null) return 'Loading power infrastructure…';
      const tierStrong = (p.subsTier?.high || 0) + (p.subsTier?.likely || 0);
      if (tierStrong < 3) {
        return `Only ${tierStrong} of ${p.subsCount} substations rated for 200 MW grid-tie. BTM is the play here. ${takeaway} transmission pipeline${takeaway === 1 ? '' : 's'} cross the phases.`;
      }
      return `${tierStrong} substations rated for 200 MW grid-tie, ${takeaway} transmission pipeline${takeaway === 1 ? '' : 's'} cross the phases.`;
    }
    case 'land': {
      const l = live?.land;
      if (!l || l.fedCount == null) return 'Loading land constraints…';
      const killers: string[] = [];
      if ((l.floodCount ?? 0) > 0) killers.push(`${l.floodCount} flood polygons`);
      if ((l.habitatCount ?? 0) > 0) killers.push(`${l.habitatCount} critical-habitat zones`);
      if (killers.length === 0) return 'No avoidance-grade land constraints in the AOI.';
      return `${killers.join(' and ')} require avoidance or mitigation. Hover each to isolate.`;
    }
    case 'fiber': {
      if (!fiberStats) return 'Carrier fiber and broadband coverage in the AOI. Hover each layer to isolate.';
      const minLabel = fiberStats.minMi < 0.05 ? '0.0 mi' : `${fiberStats.minMi.toFixed(1)} mi`;
      const maxLabel = `${fiberStats.maxMi.toFixed(1)} mi`;
      const provLabel = fiberStats.maxProviders <= 1 ? '1 provider' : `up to ${fiberStats.maxProviders} providers`;
      return `Fiber within ${minLabel} to ${maxLabel} of every eligible parcel (${provLabel} in-AOI; Lufkin metro on the AOI edge for redundancy build-out).`;
    }
    case 'water': {
      const w = live?.water;
      if (!w || w.aquiferCount == null) return 'Loading water layers…';
      return 'Closed-loop air-cooled BTM. Water is context here, not a constraint. Rendered for completeness.';
    }
    case 'infra': {
      return 'Heavy-haul and crew-access infrastructure in the AOI. Highways for turbine and transformer trailers, rail for bulk material, public-use airports for OEM field techs and investor visits.';
    }
  }
}

interface PowerLiveCounts {
  subsCount: number | null;
  subsTier: { high: number; likely: number; moderate: number; low: number; unrated: number } | null;
  linesCount: number | null;
  linesEhv: number;
  utilities: number | null;
  osmMoat: number;
}

interface LandLiveCounts {
  fedCount: number | null;
  fedTopName: string | null;
  floodCount: number | null;
  habitatCount: number | null;
  mineCount: number | null;
}

interface WaterLiveCounts {
  aquiferCount: number | null;
  aquiferTopName: string | null;
  brackishCount: number | null;
  regZoneCount: number | null;
  wetlandsCount: number | null;
  wellsCount: number | null;
}

interface LiveCounts {
  power?: PowerLiveCounts;
  land?: LandLiveCounts;
  water?: WaterLiveCounts;
}

interface Props {
  slug: string;
  onClose?: () => void;
  onParcelClick?: (parcelId: string) => void;
  // When set, the briefing renders inside the share-link viewer:
  // - Close button is hidden (no parent overlay to close)
  // - clicking a parcel polygon shows an in-map popup with summary fields
  //   from the briefing payload, instead of bubbling up to the global
  //   <ParcelDetail> panel (which would call /api/parcels/:id and is not
  //   in the share scope)
  shareToken?: string;
}

type RiskTier = 'existential' | 'resolvable' | 'configurable';
interface RiskEntry { title: string; body: string; mitigation: string; tier: RiskTier }

type ShareModalState =
  | { open: false }
  | { open: true; submitting?: boolean; error?: string | null; created?: CreateZoneShareTokenResponse };

const TIER_META: Record<RiskTier, { label: string; ink: string }> = {
  existential: { label: 'Existential', ink: REPORT.tierFail },
  resolvable: { label: 'Resolvable in 30 days', ink: REPORT.tierWarn },
  configurable: { label: 'Open product question', ink: REPORT.tierInfo },
};

function firstDeliverableMonth(curve: Array<{ date: string; volume_mmcfd: number }>): string | null {
  const byMonth = new Map<string, number>();
  for (const r of curve) {
    const m = r.date.slice(0, 7);
    byMonth.set(m, (byMonth.get(m) ?? 0) + Number(r.volume_mmcfd));
  }
  const sorted = Array.from(byMonth.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  for (const [m, v] of sorted) if (v >= FIRST_DELIVERABLE_MMCF) return m;
  return null;
}

function fmtMonth(yyyymm: string | null): string {
  if (!yyyymm) return '-';
  const [y, m] = yyyymm.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[Number(m) - 1]} ${y}`;
}

function buildOwnerColorMap(rollup: ZoneOwnershipGroup[]): Map<string, string> {
  const map = new Map<string, string>();
  let privateIndex = 0;
  for (const g of rollup) {
    if (g.owner_key === 'federal') { map.set(g.owner_key, REPORT.tertiary); continue; }
    if (g.owner_key === 'other_private') { map.set(g.owner_key, OTHER_PRIVATE_COLOR); continue; }
    if (g.owner_key === 'unknown') { map.set(g.owner_key, UNKNOWN_OWNER_COLOR); continue; }
    map.set(g.owner_key, OWNER_PALETTE[privateIndex] || REPORT.body);
    privateIndex += 1;
  }
  return map;
}

// Mirror of api-server/routes/zones.js#FEDERAL_PATTERNS so a parcel's tint
// matches its rollup row without an extra round trip.
const FEDERAL_PATTERNS: RegExp[] = [
  /^us forest service$/i,
  /^u s a national forests?$/i,
  /^united states national forest serv/i,
  /^big thicket national preserve$/i,
  /^fs san augustine$/i,
  /national forest/i,
  /^us army corps/i,
  /^us department of/i,
];

function ownerKeyForParcel(p: ZoneEligibleParcel, rollupKeys: Set<string>): string {
  if (!p.owner_name_normalized) return 'unknown';
  if (FEDERAL_PATTERNS.some((re) => re.test(p.owner_name_normalized!))) return 'federal';
  // Family groupings, must mirror api-server/routes/zones.js#classifyOwner so
  // every Crown Pine / Threetree parcel resolves to the same family key
  // and the map tint matches the family rollup row + spread card.
  if (/^crown\s*pine/i.test(p.owner_name_normalized!)) return 'crown_pine_family';
  if (/^threetree\s*southern/i.test(p.owner_name_normalized!)) return 'threetree_family';
  if (rollupKeys.has(p.owner_name_normalized)) return p.owner_name_normalized;
  return 'other_private';
}

function titleCaseOwner(s: string): string {
  if (!s) return '-';
  return s
    .split(/\s+/)
    .map((w) => (w.length <= 3 && w === w.toLowerCase() ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ')
    .replace(/\bLp$/i, 'LP')
    .replace(/\bLlc$/i, 'LLC')
    .replace(/\bInc$/i, 'Inc');
}

// Description copy paired with each map snapshot in the print output. Drawn from
// the live left-rail hint but written as static prose so the print version doesn't
// depend on the AOI-clipped fetches that drive the live counts.
const PRINT_MAP_DESCRIPTION: Record<LayerKey, string> = {
  ownership: 'Eligible single-owner parcels colored by family/counterparty. Crown Pine is the lone warm tone; federal land is shown in cool gray as a non-transactable backdrop.',
  power: 'Gas pipelines emphasized for transmission lines that cross the AOI phases; transmission grid lines and substations color-coded by capacity tier; OSM switchyards (HIFLD-missing) ringed in emerald.',
  land: 'Avoidance overlays: FEMA 100-year flood, ESA critical habitat (Endangered/Threatened), federal land boundaries, mine subsidence + AML problem features.',
  fiber: 'Carrier fiber routes (FiberLight, Zayo, Segra), IXP facilities, FCC BDC enterprise vs residential coverage hexagons, pipeline-ROW fiber co-location.',
  water: 'BTM uses zero-water closed-loop cooling, so this is context not constraint. Aquifers + BRACS brackish extents + GCD/AMA regulatory zones + USFWS NWI wetlands + TWDB wells.',
  infra: 'Heavy-haul logistics: Interstate and primary highways for turbine/transformer trailers, Class I railroads for bulk material, FAA public-use airports for OEM field techs and investor visits (corp-jet capable strips highlighted).',
};

interface PrintMapImage {
  layer: LayerKey;
  label: string;
  description: string;
  dataUrl: string;
}

export function ZoneBriefing({ slug, onClose, onParcelClick, shareToken }: Props) {
  const [data, setData] = useState<ZoneBriefingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeLayer, setActiveLayer] = useState<LayerKey>('ownership');
  const [shareModal, setShareModal] = useState<ShareModalState>({ open: false });
  const [printPreparing, setPrintPreparing] = useState(false);
  const [printMapImages, setPrintMapImages] = useState<PrintMapImage[] | null>(null);
  const mapboxRef = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchZoneBriefing(slug, controller.signal)
      .then(setData)
      .catch((e) => { if (e.name !== 'CanceledError') setError(String(e)); });
    return () => controller.abort();
  }, [slug]);

  const firstDeliverable = useMemo(
    () => data ? firstDeliverableMonth(data.commitment.ramp_curve) : null,
    [data]
  );

  // Print pipeline. Mapbox WebGL canvases don't rasterize cleanly via window.print();
  // we instead cycle the live map through each tab variant, capture the canvas as a
  // PNG via getCanvas().toDataURL(), and render those static images in a print-only
  // <section>. Print CSS hides the live map and shows the captures.
  const triggerPrint = async () => {
    const map = mapboxRef.current;
    if (!map) {
      // No live map (e.g. data still loading). Fall back to plain print.
      window.print();
      return;
    }
    setPrintPreparing(true);
    const original = activeLayer;
    const captures: PrintMapImage[] = [];
    const layers: LayerKey[] = ['ownership', 'power', 'land', 'fiber', 'water', 'infra'];
    for (const layer of layers) {
      setActiveLayer(layer);
      // Wait for React to re-render ZoneMap, ZoneMap's effect to call
      // applyLayerVisibility, and Mapbox to repaint. 350ms covers all three on
      // the slowest tab (water, which has the most layers).
      await new Promise((resolve) => setTimeout(resolve, 350));
      try {
        const dataUrl = map.getCanvas().toDataURL('image/png');
        captures.push({
          layer,
          label: LAYER_LABEL[layer],
          description: PRINT_MAP_DESCRIPTION[layer],
          dataUrl,
        });
      } catch (e) {
        console.warn('Print capture failed for layer', layer, e);
      }
    }
    setActiveLayer(original);
    setPrintMapImages(captures);
    setPrintPreparing(false);
    // One render tick so the print-only block lands in the DOM before the print
    // dialog snapshots it.
    await new Promise((resolve) => setTimeout(resolve, 80));
    window.print();
    // Drop captures from state shortly after the dialog closes so the page returns
    // to its live state for screen viewing.
    setTimeout(() => setPrintMapImages(null), 1500);
  };


  if (error) return <div className="zone-briefing" style={pageBase}>Failed to load zone: {error} {onClose && <button onClick={onClose}>×</button>}</div>;
  if (!data) return <div className="zone-briefing" style={pageBase}>Loading {slug}…</div>;

  const c = data.commitment;
  const peakBcfd = c.peak_volume_bcfd != null ? Number(c.peak_volume_bcfd) : null;
  const mastheadDateLabel = new Date().toISOString().slice(0, 10);
  // Integra co-brand only on the client-shared frozen zone (Revenant East-TX).
  // New/other zones default to Atwater-only branding; re-add per client when needed.
  const showIntegraCobrand = slug === 'revenant-east-tx';

  return (
    <div className="zone-briefing" style={pageBase}>
      <div style={stickyWrapper}>
        <header style={toolbar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <img
                src="/atwater-logo-light.svg"
                alt="Atwater Energy"
                style={{ height: 28, width: 'auto', display: 'block' }}
              />
              {showIntegraCobrand && (
                <>
                  <span style={mastheadCross}>×</span>
                  <img
                    src="/integra-logo-light.webp"
                    alt="Integra"
                    style={{ height: 16, width: 'auto', display: 'block', opacity: 0.95 }}
                  />
                </>
              )}
            </div>
            <span style={mastheadMeta}>
              Supply Zone Briefing · {c.region_label}: {c.operator_name} · {mastheadDateLabel}
            </span>
          </div>
          <div>
            {!shareToken && (
              <button style={btn} onClick={() => setShareModal({ open: true })}>Share</button>
            )}
            <button style={btn} onClick={triggerPrint} disabled={printPreparing}>
              {printPreparing ? 'Preparing…' : 'Print'}
            </button>
            {onClose && <button style={btn} onClick={onClose}>Close</button>}
          </div>
        </header>
      </div>

      {shareModal.open && (
        <ShareLinkModal
          slug={slug}
          regionLabel={c.region_label}
          state={shareModal}
          onChange={setShareModal}
          onClose={() => setShareModal({ open: false })}
        />
      )}

      <ZoneCover
        commitment={c}
        firstDeliverable={firstDeliverable}
        peakBcfd={peakBcfd}
        asm2kCount={data.pathway_to_6gw.assemblies_2k_count}
        asm2kFamilies={data.pathway_to_6gw.assemblies_2k_families}
      />

      <ZoneMap
        data={data}
        activeLayer={activeLayer}
        onLayerChange={setActiveLayer}
        onParcelClick={onParcelClick}
        shareMode={!!shareToken}
        onMapReady={(map) => { mapboxRef.current = map; }}
      />

      {/* Print-only notice: tells the IC reader where in the PDF to find the
          full-color map snapshots. Hidden on screen via CSS; shown only in
          @media print. */}
      <div className="zone-print-toc-line" aria-hidden="true">
        Full-color snapshots of all five map layers (Ownership, Power, Land, Fiber, Water) appear at the end of this PDF.
      </div>

      <PathwayTo6GW data={data} />

      <SupplyCurve commitment={c} />

      <WhyRevenantFooter commitment={c} />

      <AoiPipelinesSection data={data} />

      <SpreadSection
        spread={data.spread}
        ownerColorMap={buildOwnerColorMap(data.layers.ownershipRollup)}
        ownerRollupKeys={new Set(data.layers.ownershipRollup.map((g) => g.owner_key))}
        eligibleCount={data.meta.eligible_count}
        totalFamilies={data.pathway_to_6gw.assemblies_2k_families}
      />

      <FiberReachSection />

      <RisksSection commitment={c} />

      <PrecedentAppendix data={data} />

      {/* Print-only map block — populated by triggerPrint(); hidden on screen via CSS,
          shown in @media print to replace the interactive map with 5 static snapshots,
          each paired with the same legend the live left rail renders for that tab. */}
      {printMapImages && (
        <section id="zone-print-maps-anchor" className="zone-print-maps" aria-hidden="true">
          <h2 className="zone-print-maps-title">Map layers (full-color snapshots)</h2>
          {printMapImages.map((img) => (
            <figure key={img.layer} className="zone-print-map-figure">
              <figcaption className="zone-print-map-caption">
                <span className="zone-print-map-label">{img.label}</span>
                <span className="zone-print-map-desc">{img.description}</span>
              </figcaption>
              <div className="zone-print-map-body">
                <img src={img.dataUrl} alt={`${img.label} layer snapshot`} />
                <PrintLegendBlock
                  layer={img.layer}
                  ownershipRollup={data.layers.ownershipRollup}
                  ownerColorMap={buildOwnerColorMap(data.layers.ownershipRollup)}
                />
              </div>
            </figure>
          ))}
        </section>
      )}

      {printPreparing && (
        <div className="zone-print-overlay" role="status" aria-live="polite">
          <div className="zone-print-overlay-card">
            Preparing print view (capturing 5 map layers)…
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cover
// ─────────────────────────────────────────────────────────────────────────────

function ZoneCover({
  commitment, firstDeliverable, peakBcfd,
  asm2kCount, asm2kFamilies,
}: {
  commitment: ZoneBriefingData['commitment'];
  firstDeliverable: string | null;
  peakBcfd: number | null;
  asm2kCount: number;
  asm2kFamilies: number;
}) {
  const peakBcfdLabel = peakBcfd != null ? `${peakBcfd.toFixed(2)} Bcf/d` : null;
  const dedicatedAcLabel = commitment.dedicated_acres ? `${(Number(commitment.dedicated_acres) / 1000).toFixed(0)}k+` : '100k+';

  return (
    <section style={section}>
      <h1 style={coverH1}>
        Revenant Energy's operator plan reaches up to {peakBcfdLabel ?? 'an unconfirmed peak volume'} of East Texas gas. Phase-1 power {fmtMonth(firstDeliverable)}.
      </h1>
      <div style={coverSub}>{commitment.asset_name}</div>

      <p style={pitchLead}>
        <b>~120 miles north of Houston</b>, on the Haynesville/Bossier (second-largest US gas basin). Mineral rights tied to <b>Black Stone Minerals (NYSE: BSM)</b>, with a {commitment.inventory_years ?? 30}-year inventory across {dedicatedAcLabel} dedicated acres. The eligible single-owner bench inside the 10-mi AOI buffer is deep enough that no single counterparty has to say yes for Integra to find a campus, and no single counterparty can obstruct the deal.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20, marginTop: 24, paddingTop: 20, borderTop: `1px solid ${REPORT.border}` }}>
        <Cell big={fmtMonth(firstDeliverable)} sub={`first ~${FIRST_DELIVERABLE_MW} MW deliverable`} />
        {peakBcfdLabel != null && <Cell big={`~${peakBcfdLabel}`} sub={`peak production by ${commitment.peak_volume_year ?? '-'}`} />}
        {commitment.inventory_years != null && (
          <Cell
            big={`${commitment.inventory_years} yr`}
            sub={`drilling inventory across ${dedicatedAcLabel} dedicated ac`}
          />
        )}
        <Cell big="Unconfirmed" sub="pipeline delivery and firm transport" />
        <Cell big={`${asm2kCount}`} sub={`single-owner assemblies at 2,000+ ac across ${asm2kFamilies} owner families`} emphasis />
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Map
// ─────────────────────────────────────────────────────────────────────────────

function ZoneMap({
  data, activeLayer, onLayerChange, onParcelClick, shareMode = false, onMapReady,
}: {
  data: ZoneBriefingData;
  activeLayer: LayerKey;
  onLayerChange: (k: LayerKey) => void;
  onParcelClick?: (parcelId: string) => void;
  // When true, parcel clicks render an in-map popup (county / owner / acres /
  // available MW / composite score) instead of bubbling to the global parcel
  // detail panel; keeps the share viewer scoped to briefing-payload data.
  shareMode?: boolean;
  // Fires once after the underlying mapbox-gl Map instance is constructed.
  // Used by the parent to drive the print-capture pipeline (cycle tabs, snapshot
  // canvas via getCanvas().toDataURL()).
  onMapReady?: (map: mapboxgl.Map) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const onParcelClickRef = useRef(onParcelClick);
  const shareModeRef = useRef(shareMode);
  shareModeRef.current = shareMode;
  const activeLayerRef = useRef<LayerKey>(activeLayer);
  // Per-source cache. Heavy tabs (water especially) keep their FCs here so we
  // can park empty data in Mapbox when the tab isn't active, then re-hydrate
  // instantly on return without re-fetching. Owned by setSource() and the
  // activeLayer useEffect — nothing else should write.
  const tabDataRef = useRef<Record<string, { fc: GeoJSON.FeatureCollection; owningTab: LayerKey }>>({});
  const onMapReadyRef = useRef(onMapReady);
  onParcelClickRef.current = onParcelClick;
  activeLayerRef.current = activeLayer;
  onMapReadyRef.current = onMapReady;

  // Live counts populated after AOI-clipped fetches resolve, drives Power-tab hint copy.
  const [powerSubsCount, setPowerSubsCount] = useState<number | null>(null);
  const [powerSubsTier, setPowerSubsTier] = useState<{ high: number; likely: number; moderate: number; low: number; unrated: number } | null>(null);
  const [powerLinesCount, setPowerLinesCount] = useState<number | null>(null);
  const [powerLinesEhv, setPowerLinesEhv] = useState<number>(0);
  const [powerUtilities, setPowerUtilities] = useState<number | null>(null);
  const [powerOsmMoatCount, setPowerOsmMoatCount] = useState<number>(0);

  // Live counts populated after AOI-clipped fetches resolve, drives Land-tab hint copy.
  const [landFedCount, setLandFedCount] = useState<number | null>(null);
  const [landFedTopName, setLandFedTopName] = useState<string | null>(null);
  const [landFloodCount, setLandFloodCount] = useState<number | null>(null);
  const [landHabitatCount, setLandHabitatCount] = useState<number | null>(null);
  const [landMineCount, setLandMineCount] = useState<number | null>(null);

  // Live counts populated after AOI-clipped fetches resolve, drives Water-tab hint copy.
  const [waterWetlandsCount, setWaterWetlandsCount] = useState<number | null>(null);
  const [waterAquiferTopName, setWaterAquiferTopName] = useState<string | null>(null);
  const [waterAquiferCount, setWaterAquiferCount] = useState<number | null>(null);
  const [waterBrackishCount, setWaterBrackishCount] = useState<number | null>(null);
  const [waterRegZoneCount, setWaterRegZoneCount] = useState<number | null>(null);
  const [waterWellsCount, setWaterWellsCount] = useState<number | null>(null);

  const ownerColorMap = useMemo(() => buildOwnerColorMap(data.layers.ownershipRollup), [data.layers.ownershipRollup]);
  const ownerRollupKeys = useMemo(
    () => new Set(data.layers.ownershipRollup.map((g) => g.owner_key)),
    [data.layers.ownershipRollup]
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    let xs = 0, ys = 0, n = 0;
    for (const p of data.polygons) {
      const ring = (p.geometry.coordinates[0] as [number, number][]) || [];
      for (const [x, y] of ring) { xs += x; ys += y; n++; }
    }
    const center: [number, number] = n ? [xs / n, ys / n] : [-94.5, 31.0];

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center,
      zoom: 7.4,
      attributionControl: false,
      // Require ⌘/Ctrl + scroll to zoom so page scroll doesn't get trapped
      // when the cursor passes over the map. Mapbox shows a translucent
      // overlay ("Use ⌘ + scroll to zoom the map") on stray scroll attempts.
      cooperativeGestures: true,
      // Required for getCanvas().toDataURL() in the print path. Without this,
      // the WebGL drawing buffer is cleared after each frame and the canvas
      // export returns a blank/transparent image.
      preserveDrawingBuffer: true,
    });
    mapRef.current = map;
    onMapReadyRef.current?.(map);

    map.on('load', () => {
      // Always-visible: 10-mi AOI buffer outline (the parcel-eligibility envelope)
      if (data.aoi_buffer_geometry) {
        map.addSource('aoi-buffer', {
          type: 'geojson',
          data: { type: 'Feature', geometry: data.aoi_buffer_geometry, properties: {} },
        });
        map.addLayer({
          id: 'aoi-buffer-line',
          type: 'line',
          source: 'aoi-buffer',
          paint: {
            'line-color': REPORT.heading,
            'line-width': 1.4,
            'line-opacity': 0.55,
            'line-dasharray': [4, 3],
          },
        });
      }

      // AOI phase fills + outlines. The two-coral Crown Pine bug we chased
      // earlier was actually duplicate parcel rows (fixed in the eligible
      // CTE via DISTINCT ON), not phase-fill bleed-through. With duplicates
      // gone and Phase 3 swapped to a cool tone (REPORT.secondary), fills
      // are safe to render, they no longer share a hue with Crown Pine
      // and don't compound on overlapping parcel polygons.
      data.polygons.forEach((p) => {
        const sourceId = `aoi-${p.id}`;
        const color = PHASE_COLOR[p.polygon_kind] ?? REPORT.tertiary;
        const isOuter = p.polygon_kind === 'aoi_outer';
        map.addSource(sourceId, {
          type: 'geojson',
          data: { type: 'Feature', geometry: p.geometry, properties: {} },
        });
        if (!isOuter) {
          map.addLayer({
            id: `${sourceId}-fill`,
            type: 'fill',
            source: sourceId,
            // 0.35 fill alpha matches operator-aois-phase-fill on the main map.
            paint: { 'fill-color': color, 'fill-opacity': 0.35 },
          });
        }
        map.addLayer({
          id: `${sourceId}-line`,
          type: 'line',
          source: sourceId,
          // line-width 2.5 + opacity 0.9 mirror operator-aois-phase-line / -outer-line.
          paint: {
            'line-color': color,
            'line-width': 2.5,
            'line-opacity': 0.9,
            'line-dasharray': isOuter ? [4, 3] : [1, 0],
          },
        });
      });

      // Houston pin
      const h = data.meta.houston;
      new mapboxgl.Marker({ color: REPORT.fail })
        .setLngLat([Number(h.lng), Number(h.lat)])
        .setPopup(new mapboxgl.Popup({ offset: 12 }).setText('Houston (build-partner anchor)'))
        .addTo(map);

      // Eligible parcels, single source, multiple paint variants for different toggles
      const parcelFeatures: GeoJSON.Feature[] = data.layers.eligibleParcels.map((p) => ({
        type: 'Feature',
        geometry: p.polygon,
        properties: {
          id: p.id,
          parcel_id_local: p.parcel_id_local,
          owner: p.owner_name,
          owner_key: ownerKeyForParcel(p, ownerRollupKeys),
          owner_color: ownerColorMap.get(ownerKeyForParcel(p, ownerRollupKeys)) || REPORT.tertiary,
          county: p.county,
          acres: Number(p.effective_acres) || 0,
          flood: p.flood_zone,
          fiber_mi: p.fiber_nearest_miles,
          fiber_enterprise: p.fiber_has_enterprise,
          phase: p.phase_bucket,
          score: p.composite_score,
        },
      }));
      map.addSource('eligible-parcels', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: parcelFeatures },
      });
      map.addLayer({
        id: 'eligible-ownership-fill',
        type: 'fill',
        source: 'eligible-parcels',
        filter: ['!=', ['get', 'owner_key'], 'federal'],
        paint: { 'fill-color': ['get', 'owner_color'], 'fill-opacity': 0.55 },
      });
      map.addLayer({
        id: 'eligible-ownership-line',
        type: 'line',
        source: 'eligible-parcels',
        filter: ['!=', ['get', 'owner_key'], 'federal'],
        paint: { 'line-color': REPORT.tertiary, 'line-width': 0.4, 'line-opacity': 0.55 },
      });
      map.addLayer({
        id: 'eligible-federal-fill',
        type: 'fill',
        source: 'eligible-parcels',
        filter: ['==', ['get', 'owner_key'], 'federal'],
        layout: { visibility: 'none' },
        paint: { 'fill-color': FEDERAL_COLOR, 'fill-opacity': 0.35 },
      });
      map.addLayer({
        id: 'eligible-federal-line',
        type: 'line',
        source: 'eligible-parcels',
        filter: ['==', ['get', 'owner_key'], 'federal'],
        layout: { visibility: 'none' },
        paint: { 'line-color': FEDERAL_COLOR, 'line-width': 0.8, 'line-opacity': 0.7 },
      });

      map.addLayer({
        id: 'eligible-fiber-fill',
        type: 'fill',
        source: 'eligible-parcels',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'case',
            ['<=', ['get', 'fiber_mi'], 0.1], REPORT.pass,
            ['<=', ['get', 'fiber_mi'], 2], REPORT.caution,
            ['<=', ['get', 'fiber_mi'], 5], REPORT.accent,
            REPORT.fail,
          ],
          'fill-opacity': 0.55,
        },
      });
      map.addLayer({
        id: 'eligible-fiber-line',
        type: 'line',
        source: 'eligible-parcels',
        layout: { visibility: 'none' },
        paint: { 'line-color': REPORT.heading, 'line-width': 0.4, 'line-opacity': 0.55 },
      });

      ['eligible-ownership-fill', 'eligible-fiber-fill'].forEach((layerId) => {
        map.on('click', layerId, (ev) => {
          const f = ev.features?.[0];
          if (!f) return;
          const id = f.properties?.id as string | undefined;
          if (!id) return;
          if (shareModeRef.current) {
            // Share-link viewer: in-map popup with only briefing-payload
            // fields baked into the feature. Do NOT bubble to ParcelDetail;
            // /api/parcels/:id is intentionally out of share scope.
            const props = f.properties || {};
            const owner = (props.owner as string) || '-';
            const county = (props.county as string) || '-';
            const acres = Number(props.acres) || 0;
            const score = props.score != null ? Number(props.score) : null;
            const phase = (props.phase as string) || null;
            const phaseTxt = phase === 'phase_2' ? 'Phase 2' : phase === 'phase_3' ? 'Phase 3' : phase === 'inter_phase' ? 'Inter-phase' : '';
            const esc = (s: string) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as Record<string, string>)[c]);
            const html = `
              <div style="font-family:${SANS};font-size:11px;line-height:1.5;color:${REPORT.body};max-width:240px">
                <div style="font-weight:600;color:${REPORT.heading};margin-bottom:4px">${esc(owner)}</div>
                <div>${esc(county)} County${phaseTxt ? ' · ' + phaseTxt : ''}</div>
                <div style="margin-top:6px;border-top:1px solid ${REPORT.border};padding-top:6px;display:grid;grid-template-columns:1fr 1fr;gap:4px 12px">
                  <div><span style="color:${REPORT.tertiary}">Acres</span><br/><b>${acres.toLocaleString()}</b></div>
                  ${score != null ? `<div><span style="color:${REPORT.tertiary}">Composite</span><br/><b>${score.toFixed(1)}</b></div>` : ''}
                </div>
              </div>
            `;
            new mapboxgl.Popup({ offset: 8, maxWidth: '260px' })
              .setLngLat(ev.lngLat)
              .setHTML(html)
              .addTo(map);
            return;
          }
          onParcelClickRef.current?.(id);
        });
        map.on('mouseenter', layerId, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', layerId, () => { map.getCanvas().style.cursor = ''; });
      });

      // Electric territories, utility service areas (Power tab backdrop)
      map.addSource('electric-territories', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'electric-territories-fill',
        type: 'fill',
        source: 'electric-territories',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': REPORT.highlight,
          'fill-opacity': 0.07,
          'fill-outline-color': REPORT.secondary,
        },
      }, 'aoi-buffer-line');

      // Transmission = all segments sharing a name with any pipeline that
      // crosses the phase polygons, so the full line renders continuously.
      const transmissionNames = [...new Set((data.takeawayPipelines || []).map((tp) => tp.name))];

      // Gathering & local, base layer excluding transmission names
      map.addSource('pipelines-all', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'pipelines-all-line',
        type: 'line',
        source: 'pipelines-all',
        layout: { visibility: 'none' },
        filter: transmissionNames.length
          ? ['!', ['in', ['get', 'name'], ['literal', transmissionNames]]]
          : ['has', 'name'],
        paint: {
          'line-color': REPORT.tertiary,
          'line-width': [
            'interpolate', ['linear'], ['to-number', ['get', 'diameter_inches'], 0],
            0, 0.6,
            12, 0.9,
            24, 1.6,
            36, 2.6,
            42, 3.4,
          ],
          'line-opacity': 0.55,
        },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'pipelines-takeaway-emphasis',
        type: 'line',
        source: 'pipelines-all',
        layout: { visibility: 'none' },
        filter: transmissionNames.length
          ? ['in', ['get', 'name'], ['literal', transmissionNames]]
          : ['==', ['get', 'name'], '__none__'],
        paint: {
          'line-color': REPORT.accent,
          'line-width': [
            'interpolate', ['linear'], ['to-number', ['get', 'diameter_inches'], 0],
            0, 1.4,
            12, 1.8,
            24, 2.8,
            36, 4.2,
            42, 5.0,
          ],
          'line-opacity': 0.95,
        },
      }, 'aoi-buffer-line');

      // Transmission lines, kV-coded width (138 thin → 345+ thick), color matches main map
      map.addSource('power-transmission-lines', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'power-transmission-lines-line',
        type: 'line',
        source: 'power-transmission-lines',
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'case',
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 345], '#7c3aed',
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 230], '#4f46e5',
            '#6366f1',
          ],
          'line-width': [
            'case',
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 345], 3.0,
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 230], 1.8,
            1.0,
          ],
          'line-opacity': 0.85,
        },
      }, 'aoi-buffer-line');

      // Substations, capacity-tier-colored (Integra-actionable: which dots can absorb 200 MW)
      map.addSource('power-substations-tiered', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'power-substations-tiered-circle',
        type: 'circle',
        source: 'power-substations-tiered',
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': [
            'case',
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 345], 9,
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 230], 7,
            5,
          ],
          'circle-color': [
            'match', ['get', 'capacity_tier'],
            'high', REPORT.pass,
            'likely', '#4f46e5',
            'moderate', REPORT.caution,
            'low', REPORT.tertiary,
            REPORT.tertiary,
          ],
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.6,
          'circle-opacity': 0.95,
        },
      }, 'aoi-buffer-line');

      // OSM switchyards, emerald ring on HIFLD-missing (the moat)
      map.addSource('power-osm-switchyards', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'power-osm-switchyards-circle',
        type: 'circle',
        source: 'power-osm-switchyards',
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': [
            'case',
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 345], 8,
            ['>=', ['to-number', ['get', 'voltage_kv'], 0], 230], 6,
            4,
          ],
          'circle-color': 'transparent',
          'circle-stroke-color': [
            'case',
            ['==', ['get', 'hifld_backed'], false], '#10b981',
            REPORT.heading,
          ],
          'circle-stroke-width': 2.0,
          'circle-opacity': 0.95,
        },
      }, 'aoi-buffer-line');

      // ── Land tab ────────────────────────────────────────────────────
      // Federal lands, categorical fill (BLM / State / Private / Other Federal). East TX skews toward USFS (Davy Crockett, Sabine NF).
      map.addSource('federal-lands', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'federal-lands-fill',
        type: 'fill',
        source: 'federal-lands',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': REPORT.tertiary,
          'fill-opacity': 0.18,
        },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'federal-lands-border',
        type: 'line',
        source: 'federal-lands',
        layout: { visibility: 'none' },
        paint: {
          'line-color': REPORT.tertiary,
          'line-width': 1.4,
          'line-dasharray': [4, 2],
          'line-opacity': 0.7,
        },
      }, 'aoi-buffer-line');

      // Flood zones, 100-yr Zone A in red (Nacogdoches sits at 49% Zone A, eligibility-affecting)
      map.addSource('flood-zones', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'flood-zones-fill',
        type: 'fill',
        source: 'flood-zones',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'match', ['get', 'flood_type'],
            '100yr', '#dc2626',
            'undetermined', '#f97316',
            '#dc2626',
          ],
          'fill-opacity': 0.28,
        },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'flood-zones-outline',
        type: 'line',
        source: 'flood-zones',
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'flood_type'],
            '100yr', '#dc2626',
            'undetermined', '#f97316',
            '#dc2626',
          ],
          'line-width': 1.0,
          'line-opacity': 0.7,
        },
      }, 'aoi-buffer-line');

      // Critical habitat, ESA-listed species, color by listing_status
      map.addSource('critical-habitat', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'critical-habitat-fill',
        type: 'fill',
        source: 'critical-habitat',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'match', ['get', 'listing_status'],
            'Endangered', '#dc2626',
            'Threatened', '#f59e0b',
            '#8b5cf6',
          ],
          'fill-opacity': 0.22,
        },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'critical-habitat-border',
        type: 'line',
        source: 'critical-habitat',
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'listing_status'],
            'Endangered', '#dc2626',
            'Threatened', '#d97706',
            '#7c3aed',
          ],
          'line-width': 1.6,
          'line-dasharray': [3, 2],
          'line-opacity': 0.8,
        },
      }, 'aoi-buffer-line');

      // Mine hazards, historic underground mine subsidence + AML problem features. WV-heavy in DB, expected 0 in East TX.
      map.addSource('mine-subsidence', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'mine-subsidence-fill',
        type: 'fill',
        source: 'mine-subsidence',
        layout: { visibility: 'none' },
        paint: { 'fill-color': '#7c2d12', 'fill-opacity': 0.4 },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'mine-subsidence-border',
        type: 'line',
        source: 'mine-subsidence',
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#7c2d12',
          'line-width': 1.4,
          'line-dasharray': [2, 2],
          'line-opacity': 0.85,
        },
      }, 'aoi-buffer-line');
      map.addSource('aml-sites', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'aml-sites-fill',
        type: 'fill',
        source: 'aml-sites',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'match', ['get', 'severity'],
            'emergency', '#dc2626',
            'active', '#f97316',
            'unfunded', '#eab308',
            'completed', '#65a30d',
            '#a16207',
          ],
          'fill-opacity': 0.55,
        },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'aml-sites-border',
        type: 'line',
        source: 'aml-sites',
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'severity'],
            'emergency', '#991b1b',
            'active', '#c2410c',
            'unfunded', '#a16207',
            'completed', '#4d7c0f',
            '#78350f',
          ],
          'line-width': 1.4,
          'line-opacity': 0.95,
        },
      }, 'aoi-buffer-line');

      // ── Water tab ───────────────────────────────────────────────────
      // BTM zero-water-cooling thesis softens this tab, but render honestly:
      // Wetlands (NWI), aquifers, BRACS brackish extents, water regulatory zones, TWDB wells.
      // Aquifer fill (bottom of stack, broad teal wash)
      map.addSource('aquifer-boundaries', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'aquifer-fill',
        type: 'fill',
        source: 'aquifer-boundaries',
        layout: { visibility: 'none' },
        paint: { 'fill-color': '#06b6d4', 'fill-opacity': 0.10 },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'aquifer-outline',
        type: 'line',
        source: 'aquifer-boundaries',
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#0891b2',
          'line-width': 1.6,
          'line-dasharray': [4, 2],
          'line-opacity': 0.6,
        },
      }, 'aoi-buffer-line');

      // BRACS brackish extents, TWDB BRACS Aquifer studies
      map.addSource('brackish-zones', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'brackish-fill',
        type: 'fill',
        source: 'brackish-zones',
        layout: { visibility: 'none' },
        paint: { 'fill-color': '#14b8a6', 'fill-opacity': 0.15 },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'brackish-outline',
        type: 'line',
        source: 'brackish-zones',
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#0f766e',
          'line-width': 1.4,
          'line-dasharray': [2, 2],
          'line-opacity': 0.7,
        },
      }, 'aoi-buffer-line');

      // Water regulatory zones (TX GCDs / AZ AMAs / NM declared basins), zone_type-coded
      map.addSource('water-regulatory-zones', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'water-reg-zones-fill',
        type: 'fill',
        source: 'water-regulatory-zones',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'match', ['get', 'zone_type'],
            'gcd', '#0284c7',
            'ama', '#d97706',
            'ina', '#c48a5c',
            'declared_basin', '#b85c5c',
            'regulation', '#16a34a',
            'closure', '#dc2626',
            '#6A7E8E',
          ],
          'fill-opacity': [
            'match', ['get', 'zone_type'],
            'closure', 0.32,
            0.14,
          ],
        },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'water-reg-zones-border',
        type: 'line',
        source: 'water-regulatory-zones',
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'zone_type'],
            'gcd', '#0284c7',
            'ama', '#d97706',
            'ina', '#c48a5c',
            'declared_basin', '#b85c5c',
            'regulation', '#16a34a',
            'closure', '#dc2626',
            '#6A7E8E',
          ],
          'line-width': 1.4,
          'line-dasharray': [4, 2],
          'line-opacity': 0.6,
        },
      }, 'aoi-buffer-line');

      // Wetlands (USFWS NWI), wetland_type-coded fill (East TX is forested-shrub heavy)
      map.addSource('wetlands', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'wetlands-fill',
        type: 'fill',
        source: 'wetlands',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': [
            'match', ['get', 'wetland_type'],
            'Freshwater Pond', '#0ea5e9',
            'Lake', '#0284c7',
            'Riverine', '#6366f1',
            'Freshwater Emergent Wetland', '#22c55e',
            'Freshwater Forested/Shrub Wetland', '#15803d',
            'Estuarine and Marine Wetland', '#14b8a6',
            '#94a3b8',
          ],
          'fill-opacity': 0.28,
        },
      }, 'aoi-buffer-line');
      map.addLayer({
        id: 'wetlands-border',
        type: 'line',
        source: 'wetlands',
        layout: { visibility: 'none' },
        paint: {
          'line-color': [
            'match', ['get', 'wetland_type'],
            'Freshwater Pond', '#0ea5e9',
            'Lake', '#0284c7',
            'Riverine', '#6366f1',
            'Freshwater Emergent Wetland', '#22c55e',
            'Freshwater Forested/Shrub Wetland', '#15803d',
            'Estuarine and Marine Wetland', '#14b8a6',
            '#94a3b8',
          ],
          'line-width': 0.8,
          'line-opacity': 0.5,
        },
      }, 'aoi-buffer-line');

      // TWDB water wells, point circles (light, since BTM doesn't draw groundwater)
      map.addSource('twdb-wells', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'twdb-wells-circle',
        type: 'circle',
        source: 'twdb-wells',
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': 3.2,
          'circle-color': '#0ea5e9',
          'circle-stroke-color': '#0c4a6e',
          'circle-stroke-width': 0.8,
          'circle-opacity': 0.7,
        },
      });

      // Gas processing plants
      const gasFeatures: GeoJSON.Feature[] = data.layers.gasPlants.map((g) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [Number(g.lng), Number(g.lat)] },
        properties: { id: g.id, name: g.name },
      }));
      map.addSource('gas-plants', { type: 'geojson', data: { type: 'FeatureCollection', features: gasFeatures } });
      map.addLayer({
        id: 'gas-plants-symbols',
        type: 'circle',
        source: 'gas-plants',
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': 7,
          'circle-color': REPORT.accent,
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.5,
        },
      });

      // Carrier fiber routes, empty source, populated from /api/carrier-routes (mirrors main map paint)
      map.addSource('carrier-routes-all', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'carrier-routes-all-line',
        type: 'line',
        source: 'carrier-routes-all',
        layout: { visibility: 'none', 'line-cap': 'round' },
        paint: {
          'line-color': [
            'match', ['get', 'carrier'],
            'Zayo', '#0891b2',
            'FiberLight', '#f59e0b',
            'Segra', '#10b981',
            REPORT.pass,
          ],
          'line-width': 1.8,
          'line-opacity': 0.9,
        },
      });

      // IXP facilities (Internet Exchange Points)
      map.addSource('ixp-facilities', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'ixp-facilities-circle',
        type: 'circle',
        source: 'ixp-facilities',
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': 6,
          'circle-color': '#7c3aed',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.9,
        },
      });

      // Fiber-row pipelines, pipelines with confirmed fiber co-location
      map.addSource('fiber-row-pipelines', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'fiber-row-pipelines-line',
        type: 'line',
        source: 'fiber-row-pipelines',
        layout: { visibility: 'none' },
        paint: {
          'line-color': REPORT.tertiary,
          'line-width': 1.0,
          'line-opacity': 0.65,
          'line-dasharray': [2, 2],
        },
      });

      // Fiber coverage hexagons, same enterprise/residential split as main map
      map.addSource('fiber-hexagons', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'fiber-hexagons-fill',
        type: 'fill',
        source: 'fiber-hexagons',
        layout: { visibility: 'none' },
        paint: {
          'fill-color': ['case',
            ['==', ['get', 'has_enterprise'], true], '#0e7490',
            '#0891b2',
          ],
          'fill-opacity': ['case',
            ['==', ['get', 'has_enterprise'], true], 0.30,
            0.16,
          ],
        },
      }, 'aoi-buffer-line'); // insert UNDER the AOI buffer outline so the dashed boundary stays visible
      map.addLayer({
        id: 'fiber-hexagons-line',
        type: 'line',
        source: 'fiber-hexagons',
        layout: { visibility: 'none' },
        paint: {
          'line-color': '#0e7490',
          'line-width': 0.6,
          'line-opacity': 0.45,
        },
      }, 'aoi-buffer-line');

      // ── Logistics (Infra) tab ───────────────────────────────────────
      // Heavy-haul and crew-access infrastructure. Highways orange (turbine/transformer
      // trailers), railroads dashed amber (bulk material), airports as circles colored
      // by paved-runway tier (corp-jet vs GA).
      map.addSource('infra-highways', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'infra-highways-line',
        type: 'line',
        source: 'infra-highways',
        layout: { visibility: 'none' },
        paint: { 'line-color': '#d97706', 'line-width': 1.8, 'line-opacity': 0.85 },
      }, 'aoi-buffer-line');
      map.addSource('infra-railroads', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: 'infra-railroads-line',
        type: 'line',
        source: 'infra-railroads',
        layout: { visibility: 'none' },
        paint: { 'line-color': '#92400e', 'line-width': 1.2, 'line-opacity': 0.85, 'line-dasharray': [4, 2] },
      }, 'aoi-buffer-line');
      // Airports are pre-filtered to corp-jet capable strips only (paved runway >= 5,000 ft).
      // Dots are prominent by default since every visible airport is an actionable
      // private-jet option; tier color/size encodes which class of jet it accommodates.
      map.addSource('infra-airports', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      map.addLayer({
        id: 'infra-airports-circle',
        type: 'circle',
        source: 'infra-airports',
        layout: { visibility: 'none' },
        paint: {
          'circle-radius': [
            'case',
            ['>=', ['to-number', ['get', 'max_paved_runway_ft'], 0], 10000], 14,
            ['>=', ['to-number', ['get', 'max_paved_runway_ft'], 0], 7000], 11,
            8,
          ],
          'circle-color': [
            'case',
            ['>=', ['to-number', ['get', 'max_paved_runway_ft'], 0], 10000], '#15803d',
            ['>=', ['to-number', ['get', 'max_paved_runway_ft'], 0], 7000], '#16a34a',
            '#0891b2',
          ],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2.5,
          'circle-opacity': 0.95,
        },
      }, 'aoi-buffer-line');

      // Hover popup spells out runway specs + the amenities a corp-jet operator
      // checks before filing a flight plan (Jet A, lighting, tower, customs).
      const airportPopup = new mapboxgl.Popup({
        closeButton: false, closeOnClick: false, offset: 10, maxWidth: '280px',
        className: 'briefing-pipeline-popup',
      });
      const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
      map.on('mousemove', 'infra-airports-circle', (e) => {
        if (!e.features?.[0]) return;
        const f = e.features[0];
        map.getCanvas().style.cursor = 'pointer';
        const props = f.properties || {};
        const name = typeof props.name === 'string' ? props.name : 'Airport';
        const code = (typeof props.icao_id === 'string' && props.icao_id) || (typeof props.arpt_id === 'string' && props.arpt_id) || '';
        const maxPaved = Number(props.max_paved_runway_ft || 0);
        const maxWidth = props.max_paved_runway_width_ft != null ? Number(props.max_paved_runway_width_ft) : null;
        const maxLit = Number(props.max_lit_runway_ft || 0);
        const tier = maxPaved >= 10000 ? 'Heavy cargo capable' : maxPaved >= 7000 ? 'Heavy lift capable' : 'Corp-jet capable';
        const tierColor = maxPaved >= 10000 ? '#15803d' : maxPaved >= 7000 ? '#16a34a' : '#0891b2';
        const klass = props.airport_class === 'commercial' ? 'Commercial' : 'General aviation';
        const fuelTypes = typeof props.fuel_types === 'string' ? props.fuel_types : '';
        const hasJetA = /(^|[\s,])(A|JET[\s-]?A)([\s,]|$)/i.test(fuelTypes);
        const towerType = typeof props.tower_type === 'string' && props.tower_type ? props.tower_type : null;
        const customsRaw = typeof props.customs === 'string' ? props.customs : '';
        const lightingSchedule = typeof props.lighting_schedule === 'string' && props.lighting_schedule ? props.lighting_schedule : null;
        const airframeRepair = typeof props.airframe_repair === 'string' && props.airframe_repair ? props.airframe_repair : null;
        // Width-band annotation for the runway specs line.
        const widthNote = maxWidth != null
          ? maxWidth >= 100
            ? '<span style="color:#16a34a">wide</span>'
            : maxWidth >= 75
              ? '<span style="color:#0891b2">corp-jet standard</span>'
              : '<span style="color:#d97706">narrow — confirm with larger-jet operators</span>'
          : '';
        const rows: string[] = [];
        rows.push(`<div><strong>Runway</strong> ${maxPaved.toLocaleString()} ft${maxWidth ? ` × ${maxWidth} ft` : ''} paved${widthNote ? ` (${widthNote})` : ''}</div>`);
        rows.push(`<div><strong>Jet A fuel</strong> ${hasJetA ? '<span style="color:#16a34a">Available</span>' : '<span style="color:#d97706">Not on field — confirm with FBO</span>'}</div>`);
        rows.push(`<div><strong>Runway lighting</strong> ${maxLit >= maxPaved && maxPaved > 0 ? `Lit (${maxLit.toLocaleString()} ft${lightingSchedule ? ` · ${escapeHtml(lightingSchedule)}` : ''})` : '<span style="color:#d97706">Day-only or partial</span>'}</div>`);
        rows.push(`<div><strong>Tower</strong> ${towerType ? escapeHtml(towerType) : '<span style="color:#6b7280">Non-towered (CTAF / self-announce)</span>'}</div>`);
        rows.push(`<div><strong>Customs</strong> ${customsRaw === 'Y' ? '<span style="color:#7c3aed">Available</span>' : 'None'}</div>`);
        if (airframeRepair && airframeRepair !== 'NONE') {
          rows.push(`<div><strong>On-field maintenance</strong> ${escapeHtml(airframeRepair === 'MAJOR' ? 'Major airframe repair' : airframeRepair === 'MINOR' ? 'Minor airframe' : airframeRepair)}</div>`);
        }
        airportPopup
          .setLngLat(e.lngLat)
          .setHTML(`
            <div style="font-size:12px;color:#2E3840;line-height:1.55;min-width:240px">
              <div style="font-size:13px;font-weight:700;margin-bottom:2px">${escapeHtml(name)}${code ? ` (${escapeHtml(code)})` : ''}</div>
              <div style="font-size:10px;color:#6A7E8E;margin-bottom:6px">${escapeHtml(klass)} · <span style="color:${tierColor};font-weight:600">${tier}</span></div>
              ${rows.join('')}
            </div>
          `)
          .addTo(map);
      });
      map.on('mouseleave', 'infra-airports-circle', () => {
        map.getCanvas().style.cursor = '';
        airportPopup.remove();
      });

      // (Previously: thick grey mega-assembly outline highlighting the largest
      // single-owner contiguous mass. Removed 2026-05-01, the family color
      // now carries Crown Pine emphasis on its own, and the spread card
      // breaks down assembly counts per family. The thick outline conflicted
      // visually with the subtle eligible-ownership-line stroke.)

      applyLayerVisibility(map, activeLayerRef.current);

      // Compute AOI bbox (used for fitBounds AND for clipping main-map data layers)
      let aoiBbox: [number, number, number, number] | null = null;
      if (data.polygons.length) {
        const bounds = new mapboxgl.LngLatBounds();
        for (const poly of data.polygons) {
          const ring = poly.geometry.coordinates[0] as [number, number][];
          for (const [lng, lat] of ring) bounds.extend([lng, lat]);
        }
        map.fitBounds(bounds, { padding: 60, maxZoom: 9, duration: 0 });
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        // pad ~0.15° (~10 mi) so layer data doesn't clip at viewport edge
        aoiBbox = [sw.lng - 0.15, sw.lat - 0.15, ne.lng + 0.15, ne.lat + 0.15];
      }

      // Fetch real layer data clipped to AOI bbox, matches main map's data, not just parcel-derived metrics.
      // Use authed `api` axios helpers (raw fetch hits 401 because of the password gate).
      if (aoiBbox) {
        const bboxParam = aoiBbox.join(',');
        // Sources owned by each tab. Heavy tabs (water, with up to ~800 wetland
        // polygons + 4 wide-AOI aquifer polygons) block the main thread while
        // Mapbox renders them, so a quick water→other-tab click sits unprocessed
        // behind that render and water visually "carries over." Mitigation: cache
        // the fetched FCs in refs, and only push data into Mapbox sources when
        // the owning tab is active. Switching away clears the source to empty so
        // Mapbox has no polygons to render.
        const setSource = (id: string, fc: GeoJSON.FeatureCollection, owningTab?: LayerKey) => {
          if (owningTab) {
            tabDataRef.current[id] = { fc, owningTab };
            if (activeLayerRef.current !== owningTab) {
              // Owner tab is not active. Keep data cached in the ref and leave
              // the Mapbox source empty so it stays cheap to render.
              return;
            }
          }
          const src = map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
          if (src) src.setData(fc);
        };

        // Fiber-tab layers
        fetchCarrierRoutesGeoJSON(bboxParam, undefined, 8)
          .then((fc) => setSource('carrier-routes-all', fc))
          .catch(() => {});
        fetchIxpFacilitiesGeoJSON(bboxParam)
          .then((fc) => setSource('ixp-facilities', fc))
          .catch(() => {});
        fetchFiberRowPipelinesGeoJSON(bboxParam, undefined, 8)
          .then((fc) => setSource('fiber-row-pipelines', fc))
          .catch(() => {});
        fetchFiberGeoJSON(bboxParam)
          .then((fc) => setSource('fiber-hexagons', fc as GeoJSON.FeatureCollection))
          .catch(() => {});

        // Power-tab layers, pipeline takeaway is the BTM source; transmission/substations are the grid-tie story
        fetchPipelinesGeoJSON(undefined, bboxParam, undefined, 8)
          .then((fc) => setSource('pipelines-all', fc as GeoJSON.FeatureCollection))
          .catch(() => {});
        fetchPowerGeoJSON('substation', bboxParam, undefined, 138)
          .then((fc) => {
            setSource('power-substations-tiered', fc);
            setPowerSubsCount(fc.features.length);
            const tiers = fc.features.reduce<Record<string, number>>((acc, f) => {
              const t = (f.properties as { capacity_tier?: string })?.capacity_tier || 'unrated';
              acc[t] = (acc[t] || 0) + 1;
              return acc;
            }, {});
            setPowerSubsTier({
              high: tiers.high || 0,
              likely: tiers.likely || 0,
              moderate: tiers.moderate || 0,
              low: tiers.low || 0,
              unrated: tiers.unrated || 0,
            });
          })
          .catch(() => {});
        fetchPowerGeoJSON('transmission_line', bboxParam, undefined, 138)
          .then((fc) => {
            setSource('power-transmission-lines', fc);
            setPowerLinesCount(fc.features.length);
            const ehv = fc.features.filter((f) => Number((f.properties as { voltage_kv?: number })?.voltage_kv || 0) >= 345).length;
            setPowerLinesEhv(ehv);
          })
          .catch(() => {});
        fetchElectricTerritoriesGeoJSON(bboxParam)
          .then((fc) => {
            setSource('electric-territories', fc);
            const utilities = new Set<string>();
            (fc.features || []).forEach((f) => {
              const n = (f.properties as { company_name?: string; company_abbreviation?: string })?.company_name
                || (f.properties as { company_name?: string; company_abbreviation?: string })?.company_abbreviation;
              if (n) utilities.add(n);
            });
            setPowerUtilities(utilities.size);
          })
          .catch(() => {});
        fetchOsmSwitchyardsGeoJSON({ bbox: bboxParam, minVoltage: 138 })
          .then((fc) => {
            setSource('power-osm-switchyards', fc);
            const moat = (fc.features || []).filter((f) => (f.properties as { hifld_backed?: boolean })?.hifld_backed === false).length;
            setPowerOsmMoatCount(moat);
          })
          .catch(() => {});

        // Logistics-tab data: highways, railroads, airports.
        // Use a wider bbox than the rest of the briefing (60 mi padding vs 10 mi) so
        // routes and corp-jet strips show regional context — private planes routinely
        // fly 50-100 mi to a site, and a highway/rail line that exits the AOI still
        // matters for the truck haul.
        const sw0 = aoiBbox[0], sy0 = aoiBbox[1], ex0 = aoiBbox[2], ey0 = aoiBbox[3];
        const infraBbox = [sw0 - 0.85, sy0 - 0.85, ex0 + 0.85, ey0 + 0.85].join(','); // ~60 mi
        fetchHighwaysGeoJSON(infraBbox)
          .then((fc) => setSource('infra-highways', fc, 'infra'))
          .catch(() => {});
        fetchRailroadsGeoJSON(infraBbox)
          .then((fc) => setSource('infra-railroads', fc, 'infra'))
          .catch(() => {});
        fetchAirportsGeoJSON(infraBbox)
          .then((fc) => {
            // Filter to corp-jet capable airports only (paved runway >= 5,000 ft),
            // then flatten metadata so Mapbox paint expressions can read fields and
            // the hover popup can show the private-jet amenities at a glance.
            const flattened: GeoJSON.FeatureCollection = {
              ...fc,
              features: fc.features
                .map((f) => {
                  const rawMeta = f.properties?.metadata;
                  const meta = (typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta) || {};
                  const maxPaved = Number(meta.max_paved_runway_ft || 0);
                  if (maxPaved < 5000) return null;
                  // Longest-paved width comes from the runways[] array, not an aggregate.
                  const runways: Array<{ len_ft?: number; width_ft?: number | null; surface?: string | null }> = Array.isArray(meta.runways) ? meta.runways : [];
                  const paved = runways.filter((r) => r.surface && /^(ASPH|CONC)/i.test(r.surface));
                  const longest = paved.length ? paved.reduce((a, b) => Number(b.len_ft || 0) > Number(a.len_ft || 0) ? b : a, paved[0]) : null;
                  return {
                    ...f,
                    properties: {
                      ...f.properties,
                      arpt_id: meta.arpt_id || null,
                      icao_id: meta.icao_id || null,
                      airport_class: meta.airport_class || 'general_aviation',
                      max_paved_runway_ft: maxPaved,
                      max_paved_runway_width_ft: longest && longest.width_ft != null ? Number(longest.width_ft) : null,
                      max_lit_runway_ft: Number(meta.max_lit_runway_ft || 0),
                      fuel_types: typeof meta.fuel_types === 'string' ? meta.fuel_types : null,
                      customs: typeof meta.customs === 'string' ? meta.customs : null,
                      tower_type: typeof meta.tower_type === 'string' ? meta.tower_type : null,
                      lighting_schedule: typeof meta.lighting_schedule === 'string' ? meta.lighting_schedule : null,
                      far_139_type: typeof meta.far_139_type === 'string' ? meta.far_139_type : null,
                      airframe_repair: typeof meta.airframe_repair === 'string' ? meta.airframe_repair : null,
                    },
                  };
                })
                .filter((f): f is NonNullable<typeof f> => f !== null),
            };
            setSource('infra-airports', flattened, 'infra');
          })
          .catch(() => {});

        fetchFederalLandsGeoJSON(bboxParam, undefined, data.commitment.id)
          .then((fc) => {
            setSource('federal-lands', fc);
            setLandFedCount(fc.features.length);
            // Surface the largest federal-land tract by name to make the live hint specific to East TX (Davy Crockett / Sabine NF).
            let topName: string | null = null;
            let topAcres = 0;
            for (const f of fc.features) {
              // `acreage` is what /api/federal-lands/geojson emits (PostGIS-derived,
              // per-forest). The USFS metadata `gis_acres` is the 1.92M-ac
              // National-Forests-in-Texas admin-unit total — never surface that.
              const props = f.properties as { name?: string; acreage?: number | string } | null;
              if (!props) continue;
              const acres = Number(props.acreage ?? 0);
              const name = props.name ?? null;
              if (name && acres > topAcres) { topAcres = acres; topName = name; }
            }
            setLandFedTopName(topName);
          })
          .catch(() => {});
        fetchFloodZonesGeoJSON(bboxParam)
          .then((fc) => {
            setSource('flood-zones', fc);
            setLandFloodCount(fc.features.length);
          })
          .catch(() => {});
        fetchCriticalHabitatGeoJSON(bboxParam)
          .then((fc) => {
            setSource('critical-habitat', fc);
            setLandHabitatCount(fc.features.length);
          })
          .catch(() => {});
        Promise.all([
          fetchMineSubsidenceGeoJSON(bboxParam).catch(() => ({ type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection)),
          fetchAmlSitesGeoJSON(bboxParam).catch(() => ({ type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection)),
        ]).then(([sub, aml]) => {
          setSource('mine-subsidence', sub);
          setSource('aml-sites', aml);
          setLandMineCount((sub.features?.length || 0) + (aml.features?.length || 0));
        });

        // Water-tab layers, wetlands, aquifers, BRACS, regulatory zones, TWDB wells.
        // BTM zero-water-cooling thesis softens the conclusion text; data renders honestly.
        fetchAquifersGeoJSON(bboxParam)
          .then((fc) => {
            setSource('aquifer-boundaries', fc, 'water');
            setWaterAquiferCount(fc.features.length);
            // Surface largest-by-area aquifer name to flavor the hint copy (East TX = Gulf Coast / Carrizo-Wilcox / Trinity).
            let topName: string | null = null;
            let topArea = 0;
            for (const f of fc.features) {
              const props = f.properties as { name?: string; aquifer_name?: string; acres?: number; gis_acres?: number; area_sq_miles?: number } | null;
              if (!props) continue;
              const area = Number(props.area_sq_miles ?? props.acres ?? props.gis_acres ?? 0);
              const raw = props.name ?? props.aquifer_name ?? null;
              if (raw && area > topArea) {
                topArea = area;
                // Aquifer names come back UPPER_SNAKE_CASE (e.g. GULF_COAST). Titlecase + dehyphenate for the hint copy.
                topName = raw.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
              }
            }
            setWaterAquiferTopName(topName);
          })
          .catch(() => {});
        fetchBrackishZonesGeoJSON(bboxParam)
          .then((fc) => {
            setSource('brackish-zones', fc, 'water');
            setWaterBrackishCount(fc.features.length);
          })
          .catch(() => {});
        fetchWaterRegulatoryZonesGeoJSON(bboxParam)
          .then((fc) => {
            setSource('water-regulatory-zones', fc, 'water');
            setWaterRegZoneCount(fc.features.length);
          })
          .catch(() => {});
        fetchWetlandsGeoJSON(bboxParam)
          .then((fc) => {
            setSource('wetlands', fc, 'water');
            setWaterWetlandsCount(fc.features.length);
          })
          .catch(() => {});
        fetchTwdbWellsGeoJSON(bboxParam)
          .then((fc) => {
            setSource('twdb-wells', fc, 'water');
            setWaterWellsCount(fc.features.length);
          })
          .catch(() => {});
      }
    });

    return () => { map.remove(); mapRef.current = null; };
  }, [data, ownerColorMap, ownerRollupKeys]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    // Rehydrate / drain per-source caches as the tab changes. Sources owned by
    // the now-active tab get their cached FC pushed into Mapbox; sources owned
    // by any other tab get drained to an empty FC so Mapbox doesn't keep
    // rendering ~800 wetland polygons (or any other heavy layer) while the
    // user is looking at a different tab. Visibility is still toggled below,
    // but draining the source is what makes the tab-swap feel instant.
    const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
    for (const [id, entry] of Object.entries(tabDataRef.current)) {
      const src = map.getSource(id) as mapboxgl.GeoJSONSource | undefined;
      if (!src) continue;
      src.setData(entry.owningTab === activeLayer ? entry.fc : empty);
    }
    applyLayerVisibility(map, activeLayer);
  }, [activeLayer]);

  return (
    <section className="zone-live-map" style={{ ...section, padding: '20px 32px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h2 style={{ ...sectionH2, marginBottom: 0 }}>Map</h2>
        <div style={{ fontSize: 11, color: REPORT.tertiary, fontFamily: SANS }}>
          Phase 2 (amber) · Phase 3 (blue) · 10-mi AOI buffer (dashed) · Houston pin = build-partner anchor
        </div>
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
        {/* Vertical tab rail (left), keeps the map width narrower so cursor doesn't trap page scroll */}
        <div role="tablist" aria-orientation="vertical" style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 192, flexShrink: 0 }}>
          {(['ownership', 'power', 'land', 'fiber', 'water', 'infra'] as LayerKey[]).map((k, i) => {
            const active = activeLayer === k;
            return (
              <button
                key={k}
                role="tab"
                aria-selected={active}
                onClick={() => onLayerChange(k)}
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  fontFamily: SANS,
                  fontWeight: active ? 700 : 500,
                  color: active ? REPORT.heading : REPORT.secondary,
                  background: active ? REPORT.amberWash : 'transparent',
                  border: `1px solid ${active ? REPORT.accent : REPORT.border}`,
                  borderRadius: 3,
                  cursor: 'pointer',
                  letterSpacing: 0.3,
                  textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                <span style={{ fontSize: 9, color: REPORT.tertiary, fontWeight: 700, minWidth: 10 }}>{i + 1}</span>
                <span>{LAYER_LABEL[k]}</span>
              </button>
            );
          })}
          <div style={{ marginTop: 12, fontSize: 10, color: REPORT.secondary, fontFamily: SANS, lineHeight: 1.45, fontStyle: 'italic' }}>
            {layerHint(activeLayer, data, {
              power: {
                subsCount: powerSubsCount,
                subsTier: powerSubsTier,
                linesCount: powerLinesCount,
                linesEhv: powerLinesEhv,
                utilities: powerUtilities,
                osmMoat: powerOsmMoatCount,
              },
              land: {
                fedCount: landFedCount,
                fedTopName: landFedTopName,
                floodCount: landFloodCount,
                habitatCount: landHabitatCount,
                mineCount: landMineCount,
              },
              water: {
                aquiferCount: waterAquiferCount,
                aquiferTopName: waterAquiferTopName,
                brackishCount: waterBrackishCount,
                regZoneCount: waterRegZoneCount,
                wetlandsCount: waterWetlandsCount,
                wellsCount: waterWellsCount,
              },
            }, (() => {
              const ps = data.layers.eligibleParcels;
              const distances = ps.map((p) => p.fiber_nearest_miles).filter((v): v is number => v != null);
              if (!distances.length) return null;
              const counts = ps.map((p) => p.fiber_provider_count).filter((v): v is number => v != null);
              return {
                minMi: Math.min(...distances),
                maxMi: Math.max(...distances),
                maxProviders: counts.length ? Math.max(...counts) : 0,
              };
            })())}
          </div>
          {/* Legend, below hint, inside left rail */}
          <div style={{ marginTop: 10, borderTop: `1px solid ${REPORT.border}`, paddingTop: 8 }}>
            {activeLayer === 'ownership' && (
              <OwnershipLegend rollup={data.layers.ownershipRollup} colorMap={ownerColorMap} mapRef={mapRef} />
            )}
            {activeLayer !== 'ownership' && TAB_LEGEND[activeLayer] && (
              <TabLegend items={TAB_LEGEND[activeLayer]} tab={activeLayer} mapRef={mapRef} />
            )}
          </div>
        </div>

        {/* Map */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div ref={containerRef} style={{ height: 720, borderRadius: 4, overflow: 'hidden', border: `1px solid ${REPORT.border}` }} />
        </div>
      </div>
    </section>
  );
}

function applyLayerVisibility(map: mapboxgl.Map, active: LayerKey) {
  const set = (id: string, visible: boolean) => {
    if (!map.getLayer(id)) return;
    map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
  };
  // Parcels visible only on Ownership tab, non-Ownership tabs are layer-focused
  set('eligible-ownership-fill', active === 'ownership');
  set('eligible-ownership-line', active === 'ownership');
  set('eligible-federal-fill', false);
  set('eligible-federal-line', false);
  set('eligible-fiber-fill', false);
  set('eligible-fiber-line', false);

  // Power, backdrop, then network, then points
  set('electric-territories-fill', active === 'power');
  set('pipelines-all-line', active === 'power');
  set('pipelines-takeaway-emphasis', active === 'power');
  set('power-transmission-lines-line', active === 'power');
  set('gas-plants-symbols', active === 'power');
  set('power-substations-tiered-circle', active === 'power');
  set('power-osm-switchyards-circle', active === 'power');

  set('federal-lands-fill', active === 'land' || active === 'ownership');
  set('federal-lands-border', active === 'land' || active === 'ownership');
  if (map.getLayer('federal-lands-fill')) {
    map.setPaintProperty('federal-lands-fill', 'fill-opacity', 0.40);
  }
  set('flood-zones-fill', active === 'land');
  set('flood-zones-outline', active === 'land');
  set('critical-habitat-fill', active === 'land');
  set('critical-habitat-border', active === 'land');
  set('mine-subsidence-fill', active === 'land');
  set('mine-subsidence-border', active === 'land');
  set('aml-sites-fill', active === 'land');
  set('aml-sites-border', active === 'land');

  // Fiber, main-map parity layers
  set('carrier-routes-all-line', active === 'fiber');
  set('ixp-facilities-circle', active === 'fiber');
  set('fiber-row-pipelines-line', active === 'fiber');
  set('fiber-hexagons-fill', active === 'fiber');
  set('fiber-hexagons-line', active === 'fiber');

  // Water, broad backdrop, then regulatory zones, then wetlands + wells
  set('aquifer-fill', active === 'water');
  set('aquifer-outline', active === 'water');
  set('brackish-fill', active === 'water');
  set('brackish-outline', active === 'water');
  set('water-reg-zones-fill', active === 'water');
  set('water-reg-zones-border', active === 'water');
  set('wetlands-fill', active === 'water');
  set('wetlands-border', active === 'water');
  set('twdb-wells-circle', active === 'water');

  // Logistics — highways, railroads, airports
  set('infra-highways-line', active === 'infra');
  set('infra-railroads-line', active === 'infra');
  set('infra-airports-circle', active === 'infra');
}

// ── Legend item config ────────────────────────────────────────────────
// Each item lists the map layers it "owns." On hover, all other layers in
// the active tab dim to near-invisible. For shared layers (e.g.
// federal-lands-fill with multiple categories), `filter` specifies the
// feature property + value to keep bright.

type LegendType = 'square' | 'line' | 'dot' | 'dash' | 'dotring';
type LegendItem = {
  key: string;
  type: LegendType;
  swatch: string;
  label: string;
  layers: string[];
  filter?: { prop: string; value: string };
  group?: string;
  bold?: boolean;
};

const LAND_ITEMS: LegendItem[] = [
  { key: 'flood', type: 'square', swatch: 'rgba(220,38,38,0.28)', label: 'FEMA 100-yr flood', layers: ['flood-zones-fill', 'flood-zones-outline'], group: 'Avoidance overlays', bold: true },
  { key: 'ch-e', type: 'square', swatch: 'rgba(220,38,38,0.22)', label: 'Critical habitat, Endangered', layers: ['critical-habitat-fill', 'critical-habitat-border'], filter: { prop: 'listing_status', value: 'Endangered' }, group: 'Avoidance overlays', bold: true },
  { key: 'ch-t', type: 'square', swatch: 'rgba(245,158,11,0.22)', label: 'Critical habitat, Threatened', layers: ['critical-habitat-fill', 'critical-habitat-border'], filter: { prop: 'listing_status', value: 'Threatened' }, group: 'Avoidance overlays', bold: true },
  { key: 'fed-bounds', type: 'square', swatch: REPORT.tertiary, label: 'Federal land (USFS / NPS)', layers: ['federal-lands-fill', 'federal-lands-border'], group: 'Federal land', bold: true },
];

const WATER_ITEMS: LegendItem[] = [
  { key: 'gcd', type: 'square', swatch: 'rgba(2,132,199,0.14)', label: 'GCD', layers: ['water-reg-zones-fill', 'water-reg-zones-border'], filter: { prop: 'zone_type', value: 'gcd' }, group: 'Regulatory' },
  { key: 'wet-em', type: 'square', swatch: 'rgba(34,197,94,0.28)', label: 'Emergent wetland', layers: ['wetlands-fill', 'wetlands-border'], filter: { prop: 'wetland_type', value: 'Freshwater Emergent Wetland' }, group: 'Wetlands' },
  { key: 'wet-for', type: 'square', swatch: 'rgba(21,128,61,0.28)', label: 'Forested / shrub', layers: ['wetlands-fill', 'wetlands-border'], filter: { prop: 'wetland_type', value: 'Freshwater Forested/Shrub Wetland' }, group: 'Wetlands' },
  { key: 'wet-riv', type: 'square', swatch: 'rgba(99,102,241,0.28)', label: 'Riverine', layers: ['wetlands-fill', 'wetlands-border'], filter: { prop: 'wetland_type', value: 'Riverine' }, group: 'Wetlands' },
  { key: 'aquifer', type: 'square', swatch: 'rgba(6,182,212,0.10)', label: 'Aquifer (fresh)', layers: ['aquifer-fill', 'aquifer-outline'], group: 'Supply' },
  { key: 'brackish', type: 'square', swatch: 'rgba(20,184,166,0.15)', label: 'BRACS brackish', layers: ['brackish-fill', 'brackish-outline'], group: 'Supply' },
  { key: 'twdb', type: 'dot', swatch: '#0ea5e9', label: 'Water well', layers: ['twdb-wells-circle'], group: 'Supply' },
];

const FIBER_ITEMS: LegendItem[] = [
  { key: 'fl', type: 'line', swatch: '#f59e0b', label: 'FiberLight', layers: ['carrier-routes-all-line'], filter: { prop: 'carrier', value: 'FiberLight' }, group: 'Backbone carriers' },
  { key: 'hex-ent', type: 'square', swatch: 'rgba(14,116,144,0.30)', label: 'Enterprise fiber hex', layers: ['fiber-hexagons-fill', 'fiber-hexagons-line'], group: 'Coverage' },
  { key: 'hex-res', type: 'square', swatch: 'rgba(8,145,178,0.16)', label: 'Residential-only hex', layers: ['fiber-hexagons-fill', 'fiber-hexagons-line'], group: 'Coverage' },
  { key: 'ixp', type: 'dot', swatch: '#7c3aed', label: 'IXP facility', layers: ['ixp-facilities-circle'], group: 'Connectivity' },
  { key: 'frow', type: 'dash', swatch: REPORT.tertiary, label: 'Pipeline ROW fiber', layers: ['fiber-row-pipelines-line'], group: 'Connectivity' },
];

const POWER_ITEMS: LegendItem[] = [
  { key: 'transmission', type: 'line', swatch: REPORT.accent, label: 'Transmission (crosses phases)', layers: ['pipelines-takeaway-emphasis'], group: 'Gas' },
  { key: 'gathering', type: 'line', swatch: REPORT.tertiary, label: 'Gathering & local', layers: ['pipelines-all-line'], group: 'Gas' },
  { key: 'gas-plant', type: 'dot', swatch: REPORT.accent, label: 'Gas plant', layers: ['gas-plants-symbols'], group: 'Gas' },
  { key: 'tx-345', type: 'line', swatch: '#7c3aed', label: '345+ kV', layers: ['power-transmission-lines-line'], group: 'Grid' },
  { key: 'tx-230', type: 'line', swatch: '#4f46e5', label: '230 kV', layers: ['power-transmission-lines-line'], group: 'Grid' },
  { key: 'tx-138', type: 'line', swatch: '#6366f1', label: '138 kV', layers: ['power-transmission-lines-line'], group: 'Grid' },
  { key: 'sub-hi', type: 'dot', swatch: REPORT.pass, label: 'Sub, High', layers: ['power-substations-tiered-circle'], group: 'Substations' },
  { key: 'sub-lk', type: 'dot', swatch: '#4f46e5', label: 'Sub, Likely', layers: ['power-substations-tiered-circle'], group: 'Substations' },
  { key: 'sub-mod', type: 'dot', swatch: REPORT.caution, label: 'Sub, Moderate', layers: ['power-substations-tiered-circle'], group: 'Substations' },
  { key: 'sub-lo', type: 'dot', swatch: REPORT.tertiary, label: 'Sub, Low', layers: ['power-substations-tiered-circle'], group: 'Substations' },
  { key: 'osm-moat', type: 'dotring', swatch: '#10b981', label: 'OSM switchyard (moat)', layers: ['power-osm-switchyards-circle'], group: 'Other' },
  { key: 'osm-hifld', type: 'dotring', swatch: REPORT.heading, label: 'OSM switchyard', layers: ['power-osm-switchyards-circle'], group: 'Other' },
  { key: 'territory', type: 'square', swatch: 'rgba(80,98,114,0.08)', label: 'Utility territory', layers: ['electric-territories-fill'], group: 'Other' },
];

const INFRA_ITEMS: LegendItem[] = [
  { key: 'hwy', type: 'line', swatch: '#d97706', label: 'Interstate / primary highway', layers: ['infra-highways-line'], group: 'Heavy haul' },
  { key: 'rail', type: 'dash', swatch: '#92400e', label: 'Railroad', layers: ['infra-railroads-line'], group: 'Heavy haul' },
  { key: 'ap-jumbo', type: 'dot', swatch: '#15803d', label: 'Heavy cargo (paved ≥10,000 ft)', layers: ['infra-airports-circle'], group: 'Private-jet capable airports' },
  { key: 'ap-heavy', type: 'dot', swatch: '#16a34a', label: 'Heavy lift (paved ≥7,000 ft)', layers: ['infra-airports-circle'], group: 'Private-jet capable airports' },
  { key: 'ap-corp', type: 'dot', swatch: '#0891b2', label: 'Corp-jet (paved ≥5,000 ft)', layers: ['infra-airports-circle'], group: 'Private-jet capable airports' },
];

const TAB_LEGEND: Record<Exclude<LayerKey, 'ownership'>, LegendItem[]> = {
  land: LAND_ITEMS,
  water: WATER_ITEMS,
  fiber: FIBER_ITEMS,
  power: POWER_ITEMS,
  infra: INFRA_ITEMS,
};

const DIM = 0.03;

type LayerNormal = { id: string; prop: string; val: number | any[] };

const TAB_LAYER_NORMALS: Record<LayerKey, LayerNormal[]> = {
  ownership: [
    { id: 'eligible-ownership-fill', prop: 'fill-opacity', val: 0.55 },
    { id: 'eligible-ownership-line', prop: 'line-opacity', val: 0.55 },
    { id: 'federal-lands-fill', prop: 'fill-opacity', val: 0.40 },
    { id: 'federal-lands-border', prop: 'line-opacity', val: 0.7 },
  ],
  land: [
    { id: 'federal-lands-fill', prop: 'fill-opacity', val: 0.40 },
    { id: 'federal-lands-border', prop: 'line-opacity', val: 0.7 },
    { id: 'flood-zones-fill', prop: 'fill-opacity', val: 0.28 },
    { id: 'flood-zones-outline', prop: 'line-opacity', val: 0.7 },
    { id: 'critical-habitat-fill', prop: 'fill-opacity', val: 0.22 },
    { id: 'critical-habitat-border', prop: 'line-opacity', val: 0.8 },
  ],
  fiber: [
    { id: 'carrier-routes-all-line', prop: 'line-opacity', val: 0.9 },
    { id: 'ixp-facilities-circle', prop: 'circle-opacity', val: 0.9 },
    { id: 'fiber-row-pipelines-line', prop: 'line-opacity', val: 0.65 },
    { id: 'fiber-hexagons-fill', prop: 'fill-opacity', val: 0.30 },
    { id: 'fiber-hexagons-line', prop: 'line-opacity', val: 0.45 },
  ],
  water: [
    { id: 'aquifer-fill', prop: 'fill-opacity', val: 0.10 },
    { id: 'aquifer-outline', prop: 'line-opacity', val: 0.6 },
    { id: 'brackish-fill', prop: 'fill-opacity', val: 0.15 },
    { id: 'brackish-outline', prop: 'line-opacity', val: 0.7 },
    { id: 'water-reg-zones-fill', prop: 'fill-opacity', val: 0.14 },
    { id: 'water-reg-zones-border', prop: 'line-opacity', val: 0.6 },
    { id: 'wetlands-fill', prop: 'fill-opacity', val: 0.28 },
    { id: 'wetlands-border', prop: 'line-opacity', val: 0.5 },
    { id: 'twdb-wells-circle', prop: 'circle-opacity', val: 0.7 },
  ],
  power: [
    { id: 'electric-territories-fill', prop: 'fill-opacity', val: 0.07 },
    { id: 'pipelines-all-line', prop: 'line-opacity', val: 0.55 },
    { id: 'pipelines-takeaway-emphasis', prop: 'line-opacity', val: 0.95 },
    { id: 'power-transmission-lines-line', prop: 'line-opacity', val: 0.85 },
    { id: 'gas-plants-symbols', prop: 'circle-opacity', val: 0.95 },
    { id: 'power-substations-tiered-circle', prop: 'circle-opacity', val: 0.95 },
    { id: 'power-osm-switchyards-circle', prop: 'circle-opacity', val: 0.95 },
  ],
  infra: [
    { id: 'infra-highways-line', prop: 'line-opacity', val: 0.85 },
    { id: 'infra-railroads-line', prop: 'line-opacity', val: 0.85 },
    { id: 'infra-airports-circle', prop: 'circle-opacity', val: 0.95 },
  ],
};

function highlightLegendItem(map: mapboxgl.Map, tab: LayerKey, item: LegendItem) {
  const normals = TAB_LAYER_NORMALS[tab];
  const ownedSet = new Set(item.layers);
  for (const ln of normals) {
    if (!map.getLayer(ln.id)) continue;
    if (ownedSet.has(ln.id)) {
      if (item.filter) {
        map.setPaintProperty(ln.id, ln.prop, [
          'case', ['==', ['get', item.filter.prop], item.filter.value],
          typeof ln.val === 'number' ? Math.min(ln.val * 2, 0.9) : ln.val,
          DIM,
        ]);
      } else {
        map.setPaintProperty(ln.id, ln.prop, typeof ln.val === 'number' ? Math.min(ln.val * 1.5, 0.9) : ln.val);
      }
    } else {
      map.setPaintProperty(ln.id, ln.prop, DIM);
    }
  }
}

function clearLegendHighlight(map: mapboxgl.Map, tab: LayerKey) {
  const normals = TAB_LAYER_NORMALS[tab];
  for (const ln of normals) {
    if (!map.getLayer(ln.id)) continue;
    map.setPaintProperty(ln.id, ln.prop, ln.val);
  }
}

function LegendSwatch({ type, swatch }: { type: LegendType; swatch: string }) {
  switch (type) {
    case 'line':
      return <span style={{ width: 16, height: 3, background: swatch, display: 'inline-block', borderRadius: 1, flexShrink: 0 }} />;
    case 'dot':
      return <span style={{ width: 10, height: 10, background: swatch, display: 'inline-block', borderRadius: '50%', border: '1px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.1)', flexShrink: 0 }} />;
    case 'dash':
      return <span style={{ width: 16, height: 0, display: 'inline-block', borderTop: `2px dashed ${swatch}`, flexShrink: 0 }} />;
    case 'dotring':
      return <span style={{ width: 10, height: 10, background: 'transparent', display: 'inline-block', borderRadius: '50%', border: `2px solid ${swatch}`, flexShrink: 0 }} />;
    default:
      return <span style={{ width: 10, height: 10, background: swatch, display: 'inline-block', borderRadius: 1, border: '1px solid rgba(14,116,144,0.3)', flexShrink: 0 }} />;
  }
}

// Static legend used in the print-only map block. Pulls from the same
// LegendItem arrays the live left rail uses (TAB_LEGEND) so swatch/colors
// always match what the map renders. For the ownership tab, walks the
// ownership rollup with the parent-built color map.
function PrintLegendBlock({ layer, ownershipRollup, ownerColorMap }: {
  layer: LayerKey;
  ownershipRollup: ZoneOwnershipGroup[];
  ownerColorMap: Map<string, string>;
}) {
  if (layer === 'ownership') {
    return (
      <div className="zone-print-legend">
        <div className="zone-print-legend-title">Ownership</div>
        {ownershipRollup.map((g) => (
          <div key={g.owner_key} className="zone-print-legend-row">
            <LegendSwatch type="square" swatch={ownerColorMap.get(g.owner_key) || '#94a3b8'} />
            <span className="zone-print-legend-label">{g.owner_label}</span>
            <span className="zone-print-legend-meta">{Math.round(g.total_acres / 1000)}k ac</span>
          </div>
        ))}
      </div>
    );
  }
  const items = TAB_LEGEND[layer];
  if (!items) return null;
  let lastGroup: string | undefined;
  return (
    <div className="zone-print-legend">
      {items.map((item) => {
        const showGroup = item.group && item.group !== lastGroup;
        if (item.group) lastGroup = item.group;
        return (
          <div key={item.key}>
            {showGroup && <div className="zone-print-legend-group">{item.group}</div>}
            <div className="zone-print-legend-row">
              <LegendSwatch type={item.type} swatch={item.swatch} />
              <span className="zone-print-legend-label" style={{ fontWeight: item.bold ? 600 : 400 }}>{item.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TabLegend({ items, tab, mapRef }: { items: LegendItem[]; tab: LayerKey; mapRef: React.RefObject<mapboxgl.Map | null> }) {
  let lastGroup: string | undefined;
  // rAF-debounced hover commit. A cursor sweeping across the legend used to
  // fire N×onMouseEnter calls in a few ms, each one calling setPaintProperty
  // on every layer in the tab — Mapbox then queued one re-render per call.
  // With heavy water polygons that backlog showed up as legend highlights
  // resolving hundreds of ms after the cursor moved on. Now we record the
  // latest pending item in a ref and apply it once per animation frame, so
  // sweeping through 10 items costs one paint update instead of 10.
  const pendingRef = useRef<{ kind: 'item'; item: LegendItem } | { kind: 'clear' } | null>(null);
  const rafRef = useRef<number | null>(null);
  const flush = () => {
    rafRef.current = null;
    const map = mapRef.current;
    if (!map) { pendingRef.current = null; return; }
    const next = pendingRef.current;
    pendingRef.current = null;
    if (!next) return;
    if (next.kind === 'item') highlightLegendItem(map, tab, next.item);
    else clearLegendHighlight(map, tab);
  };
  const scheduleItem = (item: LegendItem) => {
    pendingRef.current = { kind: 'item', item };
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(flush);
  };
  const scheduleClear = () => {
    pendingRef.current = { kind: 'clear' };
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(flush);
  };
  useEffect(() => () => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', fontSize: 10, fontFamily: SANS, color: REPORT.body }}
      onMouseLeave={scheduleClear}
    >
      {items.map((item) => {
        const groupHeader = item.group && item.group !== lastGroup;
        if (item.group) lastGroup = item.group;
        return (
          <div key={item.key}>
            {groupHeader && (
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: REPORT.tertiary, paddingTop: 5, paddingBottom: 1, paddingLeft: 4 }}>
                {item.group}
              </div>
            )}
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 4px', cursor: 'pointer' }}
              onMouseEnter={() => scheduleItem(item)}
            >
              <LegendSwatch type={item.type} swatch={item.swatch} />
              <span style={{ lineHeight: 1.3, fontWeight: item.bold ? 600 : 400, color: item.bold ? REPORT.heading : REPORT.body }}>{item.label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const HERO_OWNER_COUNT = 4;

function OwnershipLegend({ rollup, colorMap, mapRef }: { rollup: ZoneOwnershipGroup[]; colorMap: Map<string, string>; mapRef: React.RefObject<mapboxgl.Map | null> }) {
  const [expanded, setExpanded] = useState(false);
  const heroes = rollup.slice(0, HERO_OWNER_COUNT);
  const tail = rollup.slice(HERO_OWNER_COUNT);
  const tailAcres = tail.reduce((s, g) => s + g.total_acres, 0);
  const showTail = expanded || tail.length === 0;

  const ownerRow = (g: ZoneOwnershipGroup, isHero: boolean) => (
    <div
      key={g.owner_key}
      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '2px 4px', borderRadius: 2, cursor: 'pointer' }}
      onMouseEnter={() => {
        const map = mapRef.current;
        if (!map || !map.getLayer('eligible-ownership-fill')) return;
        map.setPaintProperty('eligible-ownership-fill', 'fill-opacity', [
          'case', ['==', ['get', 'owner_key'], g.owner_key], 0.7, DIM,
        ]);
        map.setPaintProperty('eligible-ownership-line', 'line-opacity', [
          'case', ['==', ['get', 'owner_key'], g.owner_key], 0.9, DIM,
        ]);
        if (map.getLayer('federal-lands-fill')) {
          const show = g.owner_key === 'federal';
          map.setPaintProperty('federal-lands-fill', 'fill-opacity', show ? 0.40 : DIM);
          map.setPaintProperty('federal-lands-border', 'line-opacity', show ? 0.7 : DIM);
        }
      }}
      onMouseLeave={() => {
        const map = mapRef.current;
        if (!map) return;
        clearLegendHighlight(map, 'ownership');
      }}
    >
      <span style={{ width: 10, height: 10, background: colorMap.get(g.owner_key) || REPORT.tertiary, display: 'inline-block', borderRadius: 2, flexShrink: 0 }} />
      <span style={{ lineHeight: 1.3, fontWeight: isHero ? 600 : 400, color: isHero ? REPORT.heading : REPORT.secondary }}>
        {g.owner_label}
      </span>
      <span style={{ color: REPORT.tertiary, fontSize: 9, flexShrink: 0 }}>{Math.round(g.total_acres / 1000)}k</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 10, fontFamily: SANS, color: REPORT.body }}>
      {heroes.map((g) => ownerRow(g, true))}
      {!showTail && tail.length > 0 && (
        <div
          style={{ padding: '2px 4px', cursor: 'pointer', color: REPORT.tertiary, fontSize: 9, fontStyle: 'italic' }}
          onClick={() => setExpanded(true)}
        >
          + {tail.length} others ({Math.round(tailAcres / 1000)}k ac)
        </div>
      )}
      {showTail && tail.map((g) => ownerRow(g, false))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pathway to 6 GW
// ─────────────────────────────────────────────────────────────────────────────

function PathwayTo6GW({ data }: { data: ZoneBriefingData }) {
  const p = data.pathway_to_6gw;
  const peakGw = p.gas_2031_mw != null ? `${(p.gas_2031_mw / 1000).toFixed(1)} GW-e` : '-';
  const takeawayBcfd = p.gas_takeaway_today_bcfd ?? Number((p.gas_takeaway_today_mmcfd / 1000).toFixed(1));
  return (
    <section style={section}>
      <h2 style={sectionH2}>Gas in hand. Pipeline to be built. Land to be assembled.</h2>
      <p style={sectionLede}>
        Revenant has the gas on dedicated acreage today. Revenant's mandate is to build the takeaway onto the corridor. The AOI surfaces the single-owner blocks a buyer can put together around it.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginTop: 14 }}>
        <PathwayCard
          title="Gas supply"
          headline={`~${peakGw}`}
          headlineSub={`peak production by ${data.commitment.peak_volume_year ?? '-'}`}
          body={`Revenant ramps to ~${p.gas_2031_bcfd ?? '-'} Bcf/d on dedicated acreage by ${data.commitment.peak_volume_year ?? '2031'}. This is operator production context; pipeline delivery to a campus remains unconfirmed. 30-yr drilling inventory across 100k+ dedicated ac.`}
        />
        <PathwayCard
          title="Midstream"
          headline={`${takeawayBcfd} Bcf/d`}
          headlineSub={`takeaway system context through the AOI (${data.takeawayPipelines.length} lines)`}
          body={`${data.takeawayPipelines.length} interstate and intrastate lines geometrically intersect the 10-mi AOI buffer. Native operating and scheduled quantities, local delivery, and firm transport require operator confirmation.`}
        />
        <PathwayCard
          title="Land options"
          headline={`${p.assemblies_2k_count} assemblies`}
          headlineSub={`single-owner blocks at 2,000+ ac across ${p.assemblies_2k_families} owner families`}
          body={`${p.assemblies_5k_count} of those are 5,000+ ac, ${p.assemblies_10k_count} at 10,000+ ac. Each 5,000+ ac block alone seats a campus.`}
          emphasis
        />
      </div>
    </section>
  );
}

function PathwayCard({
  title, headline, headlineSub, body, emphasis,
}: {
  title: string; headline: string; headlineSub: string; body: string; emphasis?: boolean;
}) {
  return (
    <div style={{
      padding: 16,
      background: emphasis ? REPORT.amberWash : REPORT.surface,
      border: `1px solid ${emphasis ? REPORT.accent : REPORT.border}`,
      borderRadius: 4,
      fontFamily: SANS,
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: emphasis ? REPORT.accent : REPORT.tertiary, letterSpacing: 1.5, textTransform: 'uppercase' }}>
        {title}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 600, color: REPORT.heading, fontFamily: SERIF, letterSpacing: -0.3 }}>{headline}</div>
        <div style={{ fontSize: 11, color: REPORT.secondary, marginTop: 2 }}>{headlineSub}</div>
      </div>
      <div style={{ fontSize: 12, color: REPORT.body, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Area-wide takeaway context. Pipeline geometry and source scope are useful
// screening facts; generic ratings and utilization do not establish delivery.
// ─────────────────────────────────────────────────────────────────────────────

function AoiPipelinesSection({ data }: { data: ZoneBriefingData }) {
  const a = data.aoi_pipelines;
  if (!a || !a.summary || a.summary.total_named === 0) return null;
  const s = a.summary;
  const visiblePipes = a.pipelines.slice(0, 5);
  const parentCount = new Set(a.pipelines.map((p) => p.operator_parent).filter(Boolean)).size;

  return (
    <section style={section}>
      <h2 style={sectionH2}>{s.total_named} named pipelines cross the 10-mi AOI.</h2>
      <p style={sectionLede}>
        Diameter and operator identity establish physical corridor context. Native system or project capacity, scheduled quantity, operating capacity, and firm delivery remain source-scoped operator questions; this section does not infer available capacity from ratings or utilization.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, margin: '14px 0 6px' }}>
        <Cell big={`${s.pipelines_ge24in}`} sub="trunk lines ≥24″ by AOI geometry" emphasis />
        {parentCount > 0 && <Cell big={`${parentCount}`} sub="operator parent groups represented in the AOI" />}
        <Cell big="Unconfirmed" sub="local delivery and firm transport" />
      </div>

      <div style={{ marginTop: 18 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: REPORT.accent, letterSpacing: 1.5, textTransform: 'uppercase',
          fontFamily: SANS, marginBottom: 8,
        }}>
          Pipeline geometry and operator scope
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[
            { label: 'Trunk (≥24″)', count: s.pipelines_ge24in },
            { label: 'Other named lines', count: Math.max(0, s.total_named - s.pipelines_ge24in) },
            { label: 'Local delivery', count: null },
          ].map((row) => (
            <div key={row.label} style={{
              padding: '10px 12px',
              background: REPORT.surface,
              border: `1px solid ${REPORT.border}`,
              borderRadius: 4,
              fontFamily: SANS,
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: REPORT.heading }}>{row.label}</div>
              <div style={{ fontSize: 20, fontFamily: SERIF, color: REPORT.heading, marginTop: 4, letterSpacing: -0.2 }}>
                {row.count == null ? 'Unconfirmed' : row.count}
              </div>
              <div style={{ fontSize: 10, color: REPORT.secondary, marginTop: 2 }}>
                {row.count == null ? 'operator confirmation required' : `pipeline${row.count === 1 ? '' : 's'} by geometry`}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: REPORT.accent, letterSpacing: 1.5, textTransform: 'uppercase',
          fontFamily: SANS, marginBottom: 8,
        }}>
          Representative pipelines by geometry
        </div>
        <div style={{ overflowX: 'auto', border: `1px solid ${REPORT.border}`, borderRadius: 4 }}>
          <table className="zone-aoi-pipelines-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: SANS }}>
            <colgroup>
              <col className="zone-aoi-col-pipeline" />
              <col className="zone-aoi-col-operator" />
              <col className="zone-aoi-col-dia" />
            </colgroup>
            <thead style={{ background: REPORT.surface }}>
              <tr>
                <Th>Pipeline</Th>
                <Th>Operator</Th>
                <Th align="right">Dia</Th>
                <Th align="right">Native capacity</Th>
              </tr>
            </thead>
            <tbody>
              {visiblePipes.map((p, i) => {
                const isTE = /texas\s*eastern|hunt-?lufk|lufk-?joqn/i.test(p.name || '');
                const displayName = p.display_name || (isTE ? (p.name || '').toUpperCase() : titleCaseOwner(p.name));
                const displayOp = isTE ? (p.operator || '').toUpperCase() : titleCaseOwner(p.operator || '');
                return (
                <tr key={`${p.name}-${i}`} style={{ borderTop: `1px solid ${REPORT.border}` }}>
                  <Td>{displayName}</Td>
                  <Td muted>
                    <div>{displayOp}</div>
                    {p.operator_parent && (
                      <div style={{ fontSize: 10, color: REPORT.secondary, marginTop: 2, fontStyle: 'italic' }}>
                        Parent: {p.operator_parent}
                      </div>
                    )}
                  </Td>
                  <Td align="right">{p.diameter_in != null ? `${p.diameter_in}″` : '-'}</Td>
                  <Td align="right" emphasis>Unconfirmed</Td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {a.pipelines.length > visiblePipes.length && (
          <div style={{ fontSize: 11, color: REPORT.secondary, fontFamily: SANS, marginTop: 6 }}>
            + {a.pipelines.length - visiblePipes.length} smaller gas pipelines (gathering and feeders) below the trunk-line cut. Full list in the data room.
          </div>
        )}
        <div style={{ fontSize: 10, color: REPORT.tertiary, fontFamily: SANS, fontStyle: 'italic', marginTop: 6 }}>
          Diameter shown is the largest segment inside the 10-mi AOI buffer; the line's system-wide trunk size may be larger (e.g. NGPL Gulf Coast and LumbertonETC are 42″ on their full systems).
        </div>
      </div>

      <details style={{ marginTop: 16 }}>
        <summary style={{
          cursor: 'pointer', fontSize: 11, fontWeight: 600, color: REPORT.secondary,
          fontFamily: SANS, padding: '6px 0',
        }}>
          Methodology and sources
        </summary>
        <div style={{
          marginTop: 8, padding: 12,
          background: REPORT.surface, border: `1px solid ${REPORT.border}`, borderRadius: 4,
          fontSize: 11, color: REPORT.secondary, fontFamily: SANS, lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>What this is, and what it isn't</div>
          <div style={{ marginBottom: 10 }}>
          Diameter is a physical fact. A system rating, utilization reading, or annual flow does not establish capacity available to a new shipper or delivery to this site. Confirm operating capacity, scheduled quantity, receipt and delivery path, pressure, interconnect, and firm transportation with each operator.
          </div>

          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Observation status</div>
          <ul style={{ margin: '0 0 10px 0', paddingLeft: 18 }}>
            <li><b>Native operator observations</b>: none are shown here until a dated gas-day record includes operating capacity or scheduled quantity in Dth/d.</li>
            <li><b>Diameter</b>: largest segment represented inside the 10-mi AOI; the system trunk may differ.</li>
          </ul>

          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Sources</div>
          <ul style={{ margin: '0 0 10px 0', paddingLeft: 18 }}>
            <li><b>EBB portals</b>: NGPL/TGP <code>pipeline2.kindermorgan.com</code> · Texas Eastern <code>rtba.enbridge.com</code> (LINK) · Gulf South <code>reporting.prod.bwpmlp.org</code> (BWP)</li>
            <li><b>TX RRC</b> ArcGIS Layer 13: pipe geometry, diameters, uppercase names (e.g. HUNT-LUFK)</li>
            <li><b>EIA</b> Form-176 + state-to-state capacity: proper-case operator rollups (e.g. Texas Eastern Trans Co); same operators as RRC, different vintage</li>
            <li><b>HIFLD</b>: supplementary geometry</li>
            <li>Raw EBB CSV/XLSX archived per gas day at <code>data/snapshots/ebb/{'{'}code{'}'}/{'{'}gas_day{'}'}/</code> with sha256 metadata for provenance</li>
          </ul>

          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Operator parents disclosed</div>
          <div style={{ marginBottom: 10 }}>
            Parent company surfaced under each operator subsidiary for concentration-risk visibility:
            Kinder Morgan, Enbridge, Boardwalk, Energy Transfer, Phillips 66, Williams, EQT Corp, TC Energy, Berkshire Hathaway Energy.
          </div>

          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Acronyms</div>
          <dl className="zone-acronym-list">
            <dt>EBB</dt><dd>Electronic Bulletin Board; FERC-mandated daily public capacity / utilization disclosures by interstate gas pipeline operators.</dd>
            <dt>EIA Form-176</dt><dd>DOE/EIA's annual interstate-pipeline capacity + state-pair receipts / deliveries report.</dd>
            <dt>HIFLD</dt><dd>Homeland Infrastructure Foundation-Level Data; DHS-curated geospatial dataset of US critical infrastructure (pipelines, transmission, etc.).</dd>
            <dt>RRC</dt><dd>Texas Railroad Commission; the state regulator for oil and gas pipelines in TX (despite the name, no longer regulates railroads).</dd>
          </dl>
        </div>
      </details>
    </section>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th style={{
      textAlign: align ?? 'left',
      padding: '8px 10px',
      fontSize: 10,
      fontWeight: 700,
      color: REPORT.secondary,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      borderBottom: `1px solid ${REPORT.border}`,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </th>
  );
}

function Td({ children, align, muted, emphasis }: { children: React.ReactNode; align?: 'left' | 'right'; muted?: boolean; emphasis?: boolean }) {
  return (
    <td style={{
      textAlign: align ?? 'left',
      padding: '8px 10px',
      color: emphasis ? REPORT.heading : muted ? REPORT.secondary : REPORT.body,
      fontWeight: emphasis ? 600 : 400,
      whiteSpace: align === 'right' ? 'nowrap' : 'normal',
    }}>
      {children}
    </td>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fiber Reach
// Consultancy-grade fiber intelligence for the AOI. All claims are derived
// from research on 2026-05-03 (PeeringDB API, BGP toolkit, RADB IRR,
// ARIN whois, NTIA / TX BDO award records, vendor coverage maps). Full
// per-stream reports live in docs/research/2026-05-03-revenant-fiber-intel/.
// Static section: no DB calls, no spatial queries.
// ─────────────────────────────────────────────────────────────────────────────

function FiberReachSection() {
  return (
    <section style={section}>
      <h2 style={sectionH2}>Lit fiber is already inside this AOI today, and rendezvous is 114 miles south.</h2>
      <p style={sectionLede}>
        Two carriers serve customer ports inside this AOI today, both verifiable in ARIN's public route table. <b>Consolidated Communications (AS6300, "Fidium")</b> runs live ports for Angelina County HQ and the City of Lufkin; <b>AT&amp;T (AS7018)</b> has lit fiber to a Nacogdoches enterprise, dual-homed with Consolidated and independently verifiable. The closest hyperscaler-grade meet-me room sits 114 miles south at Houston Greenspoint, where Crusoe Energy already runs the BTM playbook a Revenant tenant would replicate.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, margin: '14px 0 6px' }}>
        <Cell big="2 + 1" sub="BGP-confirmed lit-fiber customer ports inside the 10-mi AOI buffer (Angelina Co HQ + City of Lufkin), plus a third in Nacogdoches dual-homed Consolidated + AT&amp;T. Independently verifiable in ARIN whois." emphasis />
        <Cell big="114 mi / ~2.5 ms RTT" sub="South to DataBank HOU West (fac_id 1476). 4 CDNs (Meta, Akamai, Edgio, Fastly) at 100+ Tbps each; Microsoft, Amazon, Cloudflare, Netflix also peer here. Crusoe (AS26356) and Chevron (AS7862) run enterprise ASes from this Houston carrier-hotel cluster. AZ-pair viable for synchronous replication." />
        <Cell big="1,839 fiber-mi" sub="Plains Internet's BEAD build inside Angelina + Nacogdoches alone by 2030. Plus ITC Broadband BOOT II in Trinity County: $35.6M / 3,916 locations, less than 50% complete today. $164M+ federal capital aimed at AOI counties; per-county XLSX released Dec 2025." />
        <Cell big="46 mi" sub="FiberLight long-haul already inside the 10-mi AOI buffer (30 live segments). Energy Transfer 42″ LumbertonETC + KMI NGPL Gulf Coast cross the AOI; major interstate operators typically run IRU dark-fiber along their pipeline ROW, 5 to 10× cheaper than greenfield lateral build when available." />
      </div>

      {/* Amp Z third-party validation kicker */}
      <div style={{
        marginTop: 22, padding: 14,
        background: REPORT.amberWash, border: `1px solid ${REPORT.accent}`, borderRadius: 4,
        fontSize: 13, color: REPORT.body, fontFamily: SANS, lineHeight: 1.6,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: REPORT.accent, letterSpacing: 1.5,
          textTransform: 'uppercase', marginBottom: 6,
        }}>
          Validated by a $1.05B check
        </div>
        Amp Z's published siting criteria (<code>ampz.energy/projects</code>) call for existing carrier presence and fiber density, proximity to long-haul fiber routes, and a site within ~100 miles of metro. The Revenant AOI is an exact match: 114 mi to Houston Greenspoint, BGP-confirmed lit fiber from two carriers, 46 mi of FiberLight long-haul already inside the AOI buffer. The developer that committed $1.05B to a 2.1 GW BTM campus inside this AOI used these connectivity criteria as their siting test.
      </div>

      {/* Panel 1: Engineering economics for a Revenant tenant */}
      <div style={{ marginTop: 22, fontSize: 12, fontFamily: SANS, lineHeight: 1.6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: REPORT.heading, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
          Engineering economics for a Revenant tenant
        </div>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: REPORT.body }}>
          A Revenant tenant lands a 100 Gbps wave by running a dual-path lateral ~114 fiber mi to the Houston Greenspoint cluster: DataBank HOU West for hyperscaler reach (Meta, Akamai, Edgio, Fastly each at 100+ Tbps in this room), Digital Realty 12001 N. Freeway for Tier-1 transit and as Crusoe's verified Houston peering point. City-end colo + cross-connect: $5 to 15K/month. Outside-plant build: ~200 fiber-mi for diverse paths at TX rural-corridor IRU rates of $60 to 140K/month per pair, or $230 to 690M to own outright. Pipeline-ROW IRU, when offered along the AOI buffer, is the wildcard that could halve the build cost. <i>FiberLight's 46-mi in-buffer footprint is under route-trace verification; spine vs Y-shape determines actual lateral economics.</i>
        </p>
        <p style={{ margin: '0 0 10px', fontSize: 13, color: REPORT.body }}>
          <b>Latency Lufkin → Houston Greenspoint ~2.5 ms RTT</b>, calibrated against WonderNetwork's Houston→Dallas ping reference (5.70 ms over 240 mi, 0.024 ms/mi) and corroborated by Houston→Asheville (0.022 ms/mi). Two independent methods converge inside a 0.3 ms window. Well inside the &gt;1 ms / &lt;10 ms hyperscaler AZ-pair envelope; AWS Direct Connect, Azure ExpressRoute, and Google Cloud Interconnect synchronous targets all met. A Revenant tenant lands here as a cloud zone, not an edge site.
        </p>
      </div>

      <details style={{ marginTop: 16 }}>
        <summary style={{
          cursor: 'pointer', fontSize: 11, fontWeight: 600, color: REPORT.secondary,
          fontFamily: SANS, padding: '6px 0',
        }}>
          Methodology and sources
        </summary>
        <div style={{
          marginTop: 8, padding: 12,
          background: REPORT.surface, border: `1px solid ${REPORT.border}`, borderRadius: 4,
          fontSize: 11, color: REPORT.secondary, fontFamily: SANS, lineHeight: 1.55,
        }}>
          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>How we know</div>
          <div style={{ marginBottom: 10 }}>
            Every claim in this section is anchored on two public datasets: <b>BGP route tables</b> (ARIN whois, AS-CCI-TX-CUST IRR mod 2026-03-02) and the <b>FCC Broadband Data Collection 2026-Q1</b> file. Any reader can verify a specific claim in under 5 minutes.
          </div>

          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Latency calibration</div>
          <div style={{ marginBottom: 10 }}>
            Latency derived from great-circle distance + WonderNetwork empirical baseline; calibrated, not directly measured to the AOI anchor IP. WonderNetwork Houston→Dallas: 5.70 ms over 240 mi (0.024 ms/mi); Houston→Asheville: 0.022 ms/mi (corroboration). Two methods converge inside a 0.3 ms window. RIPE Atlas in-AOI probe scheduling pending.
          </div>

          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Sources</div>
          <ul style={{ margin: '0 0 10px 0', paddingLeft: 18 }}>
            <li><b>PeeringDB API</b>: DataBank HOU West (fac_id 1476) netfac roster</li>
            <li><b>BGP</b>: bgp.he.net + ARIN RDAP whois</li>
            <li><b>RADB IRR</b>: AS-CCI-TX-CUST mod 2026-03-02</li>
            <li><b>FCC Broadband Data Collection</b> 2026-Q1</li>
            <li><b>NTIA / TX Comptroller BEAD</b>: per-county XLSX 2025-12-04</li>
            <li><b>FCC RDOF Auction 904</b></li>
            <li><b>FCC Broadband Operating LLC BOOT II</b> XLSX (ITC Trinity)</li>
            <li><b>USDA ReConnect</b> Round 4</li>
            <li><b>Zayo Network Mapbook</b> 2026-01</li>
            <li><b>RIPE Atlas</b> measurement set</li>
            <li><b>WonderNetwork</b> ping grid</li>
            <li>Per-stream reports archived at <code>docs/research/2026-05-03-revenant-fiber-intel/</code></li>
          </ul>

          <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Acronyms</div>
          <dl className="zone-acronym-list">
            <dt>BGP</dt><dd>Border Gateway Protocol; the routing announcement system that exposes "who provides service to whom" in the public internet, recorded in ARIN's whois database.</dd>
            <dt>ARIN</dt><dd>American Registry for Internet Numbers; whois records of every IP block + AS number assigned in North America. Free public lookup at <code>arin.net</code>.</dd>
            <dt>AS / ASN</dt><dd>Autonomous System / Autonomous System Number; the unique identifier each ISP and enterprise advertises to the global routing table (AT&amp;T = AS7018, Crusoe = AS26356, Chevron = AS7862).</dd>
            <dt>IXP</dt><dd>Internet Exchange Point; a colocation facility where carriers physically interconnect to exchange traffic at low latency and zero-rated cost.</dd>
            <dt>RTT</dt><dd>Round-Trip Time; the latency for a packet to reach a destination and return. AZ-pair synchronous-replication targets sit under 10 ms one-way.</dd>
            <dt>IRU</dt><dd>Indefeasible Right of Use; long-term lease of a fiber pair (typically 20 to 25 years) that conveys near-ownership rights without the operator transferring title.</dd>
            <dt>ROW</dt><dd>Right of Way; the corridor an operator holds (pipeline, rail, transmission) that often co-locates dark fiber bundles available for IRU lease.</dd>
            <dt>BDC</dt><dd>FCC's Broadband Data Collection; quarterly per-address provider/technology/speed file that supersedes the older Form 477 dataset.</dd>
            <dt>BEAD</dt><dd>Broadband Equity, Access, and Deployment program; $42.45B federal broadband infrastructure subsidy authorized by the 2021 IIJA, allocated by state.</dd>
            <dt>RDOF</dt><dd>Rural Digital Opportunity Fund; FCC reverse-auction broadband subsidy ($9.2B awarded across two phases, 2020 to 2021).</dd>
            <dt>BOOT</dt><dd>Build-Out, Operate, Transfer; an FCC broadband subsidy program structure where the awardee builds, operates for a term, then transfers infrastructure ownership.</dd>
            <dt>ILEC</dt><dd>Incumbent Local Exchange Carrier; the legacy regulated phone company in a service area (Consolidated, AT&amp;T, etc.) that carries the most fiber-to-the-premise customer base.</dd>
            <dt>AZ-pair</dt><dd>Availability-Zone pair; two cloud-region zones close enough in latency (under 10 ms RTT) to act as a synchronous-replication pair for hyperscaler workloads.</dd>
          </dl>
        </div>
      </details>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// 6-card spread
// ─────────────────────────────────────────────────────────────────────────────

function SpreadSection({
  spread, ownerColorMap, ownerRollupKeys, eligibleCount, totalFamilies,
}: {
  spread: ZoneSpreadCard[];
  ownerColorMap: Map<string, string>;
  ownerRollupKeys: Set<string>;
  eligibleCount: number;
  totalFamilies: number;
}) {
  return (
    <section className="zone-spread-section" style={section}>
      <h2 style={sectionH2}>{totalFamilies} owners can each seat a hyperscale campus on their own land. Here are the top 5.</h2>
      <p style={sectionLede}>
        <b>Five of {totalFamilies} institutional families</b> with BTM-ready 2,000+ ac assemblies in the AOI. The five below concentrate the deal; the other {Math.max(0, totalFamilies - 5)} hold the AOI's transactable depth if any of the five balks. Each card aggregates every parcel that owner controls inside the 10-mi AOI buffer, with its largest contiguous assemblies broken out. Each family is large enough to seat its own hyperscale campus, counterparty enough to write a defensible chain of title, and seasoned enough to close a clean transaction (purchase preferred; long-term surface lease with operator carve-outs as fallback).
      </p>
      <div className="zone-spread-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 12, marginTop: 14 }}>
        {spread.map((card) => (
          <SpreadCardComponent
            key={card.bucket}
            card={card}
            tint={ownerColorMap.get(ownerKeyForParcel(card.parcel, ownerRollupKeys)) || REPORT.tertiary}
          />
        ))}
      </div>
    </section>
  );
}

function SpreadCardComponent({
  card, tint,
}: {
  card: ZoneSpreadCard; tint: string;
}) {
  const p = card.parcel;
  const eff = Number(p.effective_acres);
  const ac = Number(p.acres);

  // When the API attaches a `combined` payload (e.g. Crown Pine family),
  // the headline numbers are family-wide aggregates, not the rep parcel's stats.
  const c = card.combined;
  const headlineAcres = c ? c.total_acres : Math.round(eff);
  const acresSuffix = c ? 'ac' : `ac${eff > ac ? ' (asm)' : ''}`;
  const ownerHeadline = c ? c.owner_label : titleCaseOwner(p.owner_name);
  const entityCount = c?.entities?.length ?? 1;
  const subHeadline = c
    ? `${entityCount} entit${entityCount === 1 ? 'y' : 'ies'}`
    : `${p.county} County · ${p.parcel_id_local || p.id.slice(0, 8)}`;
  const counties = c?.counties ?? (p.county ? [p.county] : []);
  const parcelCount = c?.parcel_count ?? 1;
  const assemblyCount = c?.assembly_count ?? 0;

  return (
    <div
      style={{
        background: REPORT.surface,
        border: `1px solid ${REPORT.border}`,
        borderTop: `4px solid ${tint}`,
        borderRadius: 4,
        padding: 14,
        fontFamily: SANS,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, color: REPORT.tertiary, textTransform: 'uppercase' }}>
            {card.label}
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: REPORT.heading, marginTop: 3 }}>
            {ownerHeadline}
          </div>
          <div style={{ fontSize: 11, color: REPORT.secondary, marginTop: 1 }}>
            {subHeadline}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 600, color: REPORT.heading, fontFamily: SERIF, letterSpacing: -0.3 }}>
            {headlineAcres.toLocaleString()}
          </div>
          <div style={{ fontSize: 9, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: 1 }}>
            {acresSuffix}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, fontSize: 10, paddingTop: 8, borderTop: `1px solid ${REPORT.border}` }}>
        <PriorityCell
          label="Counties"
          value={`${counties.length}`}
          sub={counties.join(', ')}
          tint={tint}
        />
        <PriorityCell
          label="Parcels"
          value={parcelCount.toLocaleString()}
        />
        <PriorityCell
          label="Assemblies"
          value={`${assemblyCount}`}
          sub="of 2,000+ ac"
        />
      </div>

      {c && c.top_tracts.length > 0 && (
        <div style={{ paddingTop: 8, borderTop: `1px solid ${REPORT.border}` }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.2, color: REPORT.tertiary, textTransform: 'uppercase', marginBottom: 6 }}>
            Largest tracts
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {c.top_tracts.map((t) => (
              <div key={t.tract_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, fontSize: 11, color: REPORT.body }}>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {titleCaseOwner(t.owner || '') || 'Unknown owner'}
                  <span style={{ color: REPORT.tertiary }}> · {t.county || '-'} · {t.is_assembly ? `${t.parcel_count} parcels` : 'single parcel'}</span>
                </div>
                <div style={{ fontWeight: 600, color: REPORT.heading, fontFamily: SERIF, fontSize: 12 }}>
                  {t.acres.toLocaleString()} ac
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PriorityCell({
  label, value, sub, warn, tint,
}: {
  label: string; value: string; sub?: string; warn?: boolean; tint?: string;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 8, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{
        fontSize: 11, fontWeight: 600,
        color: warn ? REPORT.failText : REPORT.heading,
        marginTop: 2, lineHeight: 1.2,
        textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
      }}>
        {tint && <span style={{ width: 6, height: 6, borderRadius: '50%', background: tint, display: 'inline-block', marginRight: 4 }} />}
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 9, color: warn ? REPORT.failText : REPORT.tertiary, marginTop: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Supply forward curve
// ─────────────────────────────────────────────────────────────────────────────

function SupplyCurve({ commitment }: { commitment: ZoneBriefingData['commitment'] }) {
  const peakRow = commitment.ramp_curve.reduce(
    (max, r) => Number(r.volume_mmcfd) > max.v ? { d: r.date, v: Number(r.volume_mmcfd) } : max,
    { d: '', v: 0 }
  );
  const wide = new Map<string, { date: string; 'Phase 2'?: number; 'Phase 3'?: number }>();
  for (const r of commitment.ramp_curve) {
    const row = wide.get(r.date) ?? { date: r.date };
    row[r.phase_label as 'Phase 2' | 'Phase 3'] = Number(r.volume_mmcfd);
    wide.set(r.date, row);
  }
  const series = Array.from(wide.values()).sort((a, b) => a.date.localeCompare(b.date));

  // Compute one tick per January so the X-axis stops overlapping on multi-year curves.
  const yearlyTicks = series
    .filter((r) => r.date.endsWith('-01-01') || r.date.slice(5, 7) === '01')
    .map((r) => r.date);

  // Drive the headline from operator_commitments so a refreshed Revenant deck
  // (peak_volume_bcfd / peak_volume_year) flows through automatically.
  const peakBcfd = commitment.peak_volume_bcfd != null ? Number(commitment.peak_volume_bcfd) : null;
  const peakBcfdLabel = peakBcfd != null ? `${peakBcfd.toFixed(2)} Bcf/d` : 'an unconfirmed peak volume';
  const peakYear = commitment.peak_volume_year ?? 2031;

  return (
    <section style={section}>
      <h2 style={sectionH2}>Revenant's operator plan reaches {peakBcfdLabel} by {peakYear}.</h2>
      <p style={sectionLede}>
        This curve is <b>Revenant's standalone production</b>. It is operator production context, not a delivery commitment to a campus. The AOI section above documents corridor geometry. Phase-1 power runs on Revenant's first-spud schedule or a firm-transportation contract Integra negotiates separately with a corridor operator. Carriers Revenant has signed remain undisclosed (see Risks).
        {peakRow.v > 0 && ` Peak monthly throughput: ${peakRow.v.toLocaleString()} MMcf/d at ${peakRow.d}.`}
      </p>
      {/* Live (responsive) chart for screen viewing. Hidden in @media print
          because the ResponsiveContainer's SVG pixel-coordinate system
          collapses when print CSS overrides its dimensions. */}
      <div className="zone-supply-curve-live" style={{ height: 280, marginTop: 8 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={REPORT.rule as string} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: REPORT.secondary }}
              tickFormatter={(d) => d.slice(0, 4)}
              ticks={yearlyTicks.length ? yearlyTicks : undefined}
              interval={yearlyTicks.length ? 0 : 'preserveStartEnd'}
            />
            <YAxis tick={{ fontSize: 11, fill: REPORT.secondary }} label={{ value: 'MMcf/d', angle: -90, position: 'insideLeft', fontSize: 11, fill: REPORT.secondary }} />
            <Tooltip />
            <Legend />
            <ReferenceLine y={1000} stroke={REPORT.fail} strokeDasharray="4 4" label={{ value: '1 Bcf/d', position: 'right', fill: REPORT.fail, fontSize: 11 }} />
            <Area type="monotone" dataKey="Phase 2" stackId="1" stroke={REPORT.accent} fill={REPORT.accent} fillOpacity={0.55} />
            <Area type="monotone" dataKey="Phase 3" stackId="1" stroke={REPORT.highlight} fill={REPORT.highlight} fillOpacity={0.55} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Fixed-pixel chart for print. AreaChart receives explicit width/height
          (no ResponsiveContainer) so Recharts' SVG pixel positioning stays
          intact when @media print activates. 700x240 fits inside US Letter
          portrait content width (~720px at 96dpi). */}
      <div className="zone-supply-curve-print" style={{ marginTop: 8 }}>
        <AreaChart width={700} height={260} data={series} margin={{ top: 8, right: 60, left: 8, bottom: 56 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={REPORT.rule as string} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: REPORT.secondary }}
            tickFormatter={(d) => d.slice(0, 4)}
            ticks={yearlyTicks.length ? yearlyTicks : undefined}
            interval={yearlyTicks.length ? 0 : 'preserveStartEnd'}
          />
          <YAxis tick={{ fontSize: 11, fill: REPORT.secondary }} label={{ value: 'MMcf/d', angle: -90, position: 'insideLeft', fontSize: 11, fill: REPORT.secondary }} />
          <Legend wrapperStyle={{ bottom: 0, paddingTop: 8 }} />
          <ReferenceLine y={1000} stroke={REPORT.fail} strokeDasharray="4 4" label={{ value: '1 Bcf/d', position: 'right', fill: REPORT.fail, fontSize: 11 }} />
          <Area type="monotone" dataKey="Phase 2" stackId="1" stroke={REPORT.accent} fill={REPORT.accent} fillOpacity={0.55} />
          <Area type="monotone" dataKey="Phase 3" stackId="1" stroke={REPORT.highlight} fill={REPORT.highlight} fillOpacity={0.55} />
        </AreaChart>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// The team has done this before (formerly "Why Revenant?")
// ─────────────────────────────────────────────────────────────────────────────

function WhyRevenantFooter({
  commitment,
}: {
  commitment: ZoneBriefingData['commitment'];
}) {
  const TEAM = [
    { name: 'Matthew Marziani', role: 'Managing Partner', bio: 'Former Co-President of Aethon Energy; played a central role transforming it into the largest private natural gas producer in the U.S.' },
    { name: 'Monty Ward', role: 'Managing Partner', bio: 'Former General Counsel of Aethon and Partner at Weil, Gotshal & Manges LLP.' },
    { name: 'Paul Sander', role: '', bio: 'Former COO of Aethon Energy and VP of Mid-Continent Business Unit at Encana. Built two Haynesville businesses exceeding 1 to 2 Bcf/d net.' },
    { name: 'Mike Warren', role: 'COO', bio: 'Former SVP of Drilling & Completions at Aethon; grew the program to 18 rigs and 5 frac fleets in the Haynesville Shale.' },
    { name: 'Chelsea Budowsky', role: 'SVP Land', bio: 'Former VP of Land at Aethon. Acquisitions and divestitures, title examination, surface and midstream acquisition.' },
  ];
  return (
    <section style={section}>
      <h2 style={sectionH2}>Revenant's team built America's largest private gas producer.</h2>
      <p style={sectionLede}>
        Aethon Energy alumni built the largest private natural gas producer in the U.S., with two prior successful Black Stone JVs developing the surrounding acreage.
        {commitment.drill_schedule_notes && <> <b>Drill schedule:</b> {commitment.drill_schedule_notes}</>}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginTop: 12 }}>
        {TEAM.map((m) => (
          <div key={m.name} style={{ fontSize: 11, padding: 10, background: REPORT.surface, borderRadius: 4, fontFamily: SANS }}>
            <div style={{ fontWeight: 600, color: REPORT.heading, fontSize: 12 }}>{m.name}</div>
            <div style={{ color: REPORT.secondary, fontSize: 9, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{m.role}</div>
            <div style={{ lineHeight: 1.45, color: REPORT.body }}>{m.bio}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Risks
// ─────────────────────────────────────────────────────────────────────────────

const RISKS: RiskEntry[] = [
  {
    tier: 'existential',
    title: 'Pre-production operator',
    body: 'Phase 1 wells have not yet been drilled. Type curve is forward-looking. First spuds expected Jan/May 2027.',
    mitigation: 'Stage Integra build commitment to Revenant first-spud milestones; require 90-day production validation before final builder LOI. Multiple named corridor pipelines may provide separate Phase-1 counterparties, subject to operator confirmation of scheduled quantity, receipt/delivery path, and firm transportation.',
  },
  {
    tier: 'existential',
    title: 'Type curve realization',
    body: 'Single-well economics in the deck assume $2.75 flat gas. At lower realizations the development pace slows and the ramp shifts right.',
    mitigation: 'Standard structure for a pre-production operator counterparty: a floor-price gas contract or take-or-pay against the first ~250 MW caps Integra\'s commodity exposure on the early ramp; commodity upside flows to Revenant on volumes above the take-or-pay floor. Subject to negotiation with Revenant.',
  },
  {
    tier: 'resolvable',
    title: 'Public Utility Commission of Texas (PUCT) draft Rule 25.194 (March 2026) gates BTM co-location',
    body: 'Texas SB 6 (effective June 2025) already requires 75 MW+ ERCOT loads to demonstrate site control, financial assurance, and accept disconnect protocols. PUCT draft Rule 25.194 goes further: 30 days from ERCOT study completion to execute Interconnection Agreement or lose queue position, plus PUCT approval of any net-metering or BTM co-location structure. Project Lufkin is the live local stress test.',
    mitigation: 'Engage PUCT counsel pre-LOI to file the BTM co-location structure for early review; track Project Lufkin\'s comment-period submissions as the working template; structure the Revenant gas dedication so site control + financial assurance are pre-positioned the moment the ERCOT study completes.',
  },
  {
    tier: 'configurable',
    title: 'Watch: AG opinion RQ-0634-KP and 2027 legislative session',
    body: 'Texas AG opinion RQ-0634-KP (filed Feb 2026 by Delta County\'s attorney; Hood County filed a parallel request the same week) asks whether Texas counties can impose moratoria on energy and data facilities. Counties currently lack that authority by statute. If granted, Polk County\'s March 2026 oversight resolution and any East TX neighbor could harden into binding restrictions. The 2027 Texas legislative session has interim charges on data center water + grid impact (Senate Finance + Natural Resources). New disclosure laws are likely.',
    mitigation: 'Plan a proactive county-engagement playbook for Angelina + adjacent counties before public announcement (community comms is a real workstream even without a zoning hook). Build the 2027 disclosure regime into the data-room template now; budget for water + grid impact filings.',
  },
];

function RisksSection({ commitment }: { commitment: ZoneBriefingData['commitment'] }) {
  return (
    <section style={section}>
      <h2 style={sectionH2}>Risks</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {RISKS.map((r) => {
          const t = TIER_META[r.tier];
          const mitigation = r.mitigation;
          return (
            <div key={r.title} style={{ padding: 14, background: REPORT.surface, borderRadius: 3, fontSize: 13, lineHeight: 1.5, fontFamily: SANS }}>
              <TierEyebrow color={t.ink} marginBottom={10}>{t.label}</TierEyebrow>
              <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>{r.title}</div>
              <div style={{ color: REPORT.body }}>{r.body}</div>
              <div style={{ marginTop: 6, fontSize: 12, color: REPORT.body }}>
                <b style={{ color: t.ink }}>Mitigation.</b> {mitigation}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 16, fontSize: 11, color: REPORT.tertiary, fontFamily: SANS }}>
        Operator data: {commitment.source_filename || '-'} ({commitment.source_date || '-'}). Risk assessments compiled by Atwater Energy Co.
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Precedent appendix (IC-credibility scaffolding)
// ─────────────────────────────────────────────────────────────────────────────

type Verdict = 'strong' | 'parity' | 'open' | 'na';
const VERDICT_META: Record<Verdict, { label: string; bg: string; fg: string }> = {
  strong: { label: 'Stronger', bg: REPORT.pass, fg: REPORT.white },
  parity: { label: 'Parity', bg: REPORT.highlight, fg: REPORT.white },
  open:   { label: 'Open',   bg: REPORT.caution, fg: REPORT.white },
  na:     { label: 'N/A',    bg: REPORT.tertiary, fg: REPORT.white },
};

function PrecedentAppendix({ data }: { data: ZoneBriefingData }) {
  // Derive every Revenant-side number from the live API so a refreshed EBB / pipeline
  // backfill flows through automatically. Per master-brief framing: AOI acreage signals
  // option depth for Integra, never an acreage Pipeline Prospector or Integra owns.
  const a2k = data.pathway_to_6gw.assemblies_2k_count;
  const a5k = data.pathway_to_6gw.assemblies_5k_count;
  const a10k = data.pathway_to_6gw.assemblies_10k_count;
  const dedicatedKAc = data.commitment.dedicated_acres
    ? Math.round(Number(data.commitment.dedicated_acres) / 1000)
    : null;
  // Operator commitment-derived strings so the appendix tracks operator_commitments
  // edits instead of drifting from hardcoded "1.2 Bcf/d by 2031".
  const peakBcfdNum = data.commitment.peak_volume_bcfd != null ? Number(data.commitment.peak_volume_bcfd) : null;
  const peakBcfdLabel = peakBcfdNum != null ? `${peakBcfdNum} Bcf/d` : '1.2 Bcf/d';
  const peakNativeAppendix = peakBcfdNum != null ? `${peakBcfdNum.toFixed(2)} Bcf/d` : 'unconfirmed peak volume';
  const peakYearLabel = data.commitment.peak_volume_year ?? 2031;

  const aoiPipes = data.aoi_pipelines?.pipelines || [];
  const takeawayLines = data.takeawayPipelines.length;
  const takeawayBcfd = data.pathway_to_6gw.gas_takeaway_today_bcfd
    ?? Number((data.pathway_to_6gw.gas_takeaway_today_mmcfd / 1000).toFixed(1));
  const subsCount = data.layers.substations.length;

  const findPipe = (re: RegExp) => aoiPipes.find((p) => p.name && re.test(p.name));
  const ngpl = findPipe(/NGPL.*GULF/i);
  const hollyShelby = findPipe(/HOLLY.*SHELBY/i) || findPipe(/HOLLY/i);
  const trunkBits: string[] = [];
  if (ngpl) trunkBits.push(`NGPL Gulf Coast ${ngpl.diameter_in ? `${ngpl.diameter_in}″` : ''} line`.trim());
  if (hollyShelby) trunkBits.push(`Holly/Shelby ${hollyShelby.diameter_in ? `${hollyShelby.diameter_in}″` : ''} line`.trim());
  const trunkPhrase = trunkBits.length ? `${trunkBits.join(' + ')}; ` : '';

  const heroPrecedent = {
    name: 'Amp Z Project Lufkin',
    sub: 'Angelina County, TX (inside this AOI, 120 mi north of Houston)',
    capacity: '2.1 GW IT by 2029 (1.1 GW grid by 2028 + ~1 GW BTM gas)',
    acres: '1,041 ac under option (expandable to 4,000)',
    acPerGw: '~496 ac/GW (campus)',
    body: 'Former Southland Paper Mill site on SH-103 east of Lufkin. Day-one ~55 MW from re-energizing the Paper Mill\'s existing on-site substation; 175 MW by Q4 2026; 1.1 GW grid-connected by 2028 (Entergy Texas; ERCOT "Batch Zero" large-load process in flight); full 2.1 GW by 2029, layering ~1 GW of BTM gas on top of the 1.1 GW grid leg (hybrid, not pure off-grid). $1.05B private capital committed. Developer Amp Z is a subsidiary of Amp Energy ($6B / 14 GW deployed historically; Carlyle-backed). Live local stress-test of TX SB 6 (75 MW+ ERCOT large-load rules), PUCT draft Rule 25.194 (March 2026), and TCEQ permitting in unincorporated Angelina County. Separately: 1606 Corp (OTC: CBDW) parallel 55 MW campus on 132 ac at 555 Sellers Street (different site, repurposed captive power plant; coincidentally also 55 MW). Facts current as of 2026-05-04.',
  };

  const revenantAcres = `${a2k} assemblies @ 2,000+ ac in 10-mi AOI buffer (${a5k} of those at 5,000+; ${a10k} at 10,000+)`;
  const revenantGas = `Haynesville/Bossier; ${peakBcfdLabel} by ${peakYearLabel}; ${trunkPhrase}${takeawayLines} takeaway pipelines through AOI; ${takeawayBcfd} Bcf/d total takeaway context; local delivery and firm transport unconfirmed`;
  const revenantLand = `${a2k} single-owner assemblies in the AOI; deal can route through any of the institutional timber holders (Crown Pine, Rayonier, Threetree, Ingka, Paces Creek). Structure TBD pending Integra siting.`;

  const COMPARE_ROWS: Array<{ label: string; sub?: string; context?: string; mw: string; acres: string; acPerGw: string; gas: string; permit: string; land: string; emphasis?: boolean; inAoi?: boolean }> = [
    {
      label: 'Crusoe @ Lancium',
      sub: 'Taylor County, TX',
      context: 'Microsoft + Oracle + OpenAI (Stargate) tenants. Phase 2 vertical Q1 2026. $3.4B JV with Blue Owl + Primary Digital.',
      mw: '1.2 GW IT + 360 MW BTM',
      acres: '~1,100',
      acPerGw: '~917',
      gas: 'Pipeline operator not public; 10 simple-cycle turbines (GE Vernova + Solar/Caterpillar)',
      permit: 'TCEQ issued (~360 MW BTM)',
      land: 'Lancium owns master, leases (~90 ac to Crusoe)',
    },
    {
      label: 'Pacifico GW Ranch',
      sub: 'Pecos County, TX',
      context: 'First power Q1 2027, full 5+ GW by 2031. Unnamed hyperscaler tenant.',
      mw: '5 GW firm + 1.8 GW battery + 750 MW solar (7.65 GW permit)',
      acres: '8,000+',
      acPerGw: '~1,600 (firm)',
      gas: 'Permian gas; multi-lateral + dedicated 15-mi 1 Bcf/d line to Waha',
      permit: 'TCEQ 7.65 GW Jan 2026 (largest US air permit ever issued)',
      land: 'Pacifico controls; terms N/A',
    },
    {
      label: 'Fermi Matador',
      sub: 'Amarillo / Carson County, TX',
      context: 'Cornerstone tenant pulled $150M deal early 2026; share price cratered. No signed anchor as of Q1 2026.',
      mw: '11 GW IT (17 GW total stack)',
      acres: '5,769–7,570',
      acPerGw: '~524–688',
      gas: '~50% gas + 40–45% nuclear (4× AP1000) + solar',
      permit: 'TCEQ 6 GW Feb 2026; 5 GW more pending',
      land: 'Fermi-owned',
    },
    {
      label: 'Amp Z Project Lufkin (in-AOI)',
      sub: 'Angelina County, TX (inside this AOI)',
      context: '$1.05B committed; Amp Z (Carlyle-backed). Day-one ~55 MW from re-energized Paper Mill substation. See hero card above for full detail.',
      mw: '2.1 GW IT (1.1 GW grid by 2028 + ~1 GW BTM)',
      acres: '1,041 (expandable to 4,000)',
      acPerGw: '~496',
      gas: 'Hybrid: 1.1 GW Entergy grid + ~1 GW BTM gas. ERCOT Batch Zero in flight.',
      permit: 'TCEQ engagement in flight; Angelina Co unincorporated (no zoning)',
      land: 'Amp Z site under option (private/family)',
      inAoi: true,
    },
    {
      label: 'Revenant East TX (target)',
      mw: `${peakNativeAppendix} (operator plan)`,
      acres: revenantAcres,
      acPerGw: 'n/a (pre-acquisition)',
      gas: revenantGas,
      permit: 'Pending; AOI in attainment for all NAAQS',
      land: revenantLand,
      emphasis: true,
    },
  ];

  const AUDIT_ROWS: Array<{ factor: string; precedent: string; revenant: string; verdict: Verdict }> = [
    {
      factor: 'Pipeline takeaway at scale',
      precedent: 'Crusoe pipeline undisclosed; Pacifico dedicated 15-mi 1 Bcf/d to Waha; Fermi gas+nuclear stack',
      revenant: `Revenant adds ${peakBcfdLabel} into this corridor by ${peakYearLabel} and signs the takeaway contracts directly (LOIs will name carriers; currently inferred-proximity). On top: ${takeawayLines} takeaway pipelines already crossing AOI; ${takeawayBcfd} Bcf/d total corridor takeaway context, with local delivery and firm transport unconfirmed. ${trunkPhrase ? trunkPhrase.replace(/; $/, '.') : ''}`.trim(),
      verdict: 'strong',
    },
    {
      factor: 'Producing basin proximity',
      precedent: 'Permian-adjacent (Crusoe), on the Permian (Pacifico, Fermi)',
      revenant: `Revenant produces inside the Haynesville/Bossier, the 2nd largest US gas basin. Direct in-basin operator: no upstream price-shock exposure between the wellhead and Integra's campus. ${peakBcfdLabel} in-AOI production by ${peakYearLabel}.`,
      verdict: 'strong',
    },
    {
      factor: 'Contiguous land options at scale',
      precedent: '1,100 ac (Crusoe) / 8,000 ac (Pacifico) / 5,769–7,570 ac (Fermi)',
      revenant: `Revenant\'s BSM mineral dedication overlays the AOI; surface negotiated separately with timber holders (purchase preferred; long-term surface lease as fallback). ${a2k} single-owner contiguous assemblies of 2,000+ ac inside the 10-mi AOI buffer; ${a5k} at 5,000+ ac, ${a10k} at 10,000+ ac. Each 5,000+ ac block alone seats a full BTM campus.`,
      verdict: 'strong',
    },
    {
      factor: 'Counterparty depth',
      precedent: 'Lancium master leases / Pacifico controls / Fermi-owned (single-counterparty deals)',
      revenant: 'Revenant brings BSM (single mineral counterparty, NYSE: BSM) plus Aethon-era operator-side relationships across the area. Five institutional timber holders concentrate the surface side; deal can route through any of them. No single counterparty has to say yes; no single counterparty can obstruct.',
      verdict: 'strong',
    },
    {
      factor: 'TCEQ air permit',
      precedent: 'All three issued (Crusoe BTM; Pacifico 7.65 GW; Fermi 6 GW)',
      revenant: '7-county AOI in NAAQS attainment for all 7 pollutants; cleaner path than ozone-nonattainment metros. Project Lufkin\'s active TCEQ engagement provides a working local template. Revenant\'s upstream operator permits (compressor stations, potential gas-processing co-location) are precedent for their footprint; campus permit is Integra\'s filing.',
      verdict: 'open',
    },
    {
      factor: 'Build partner / generation tech',
      precedent: 'GE Vernova + Solar Turbines (Crusoe); OEM not disclosed (Pacifico); AP1000 + gas (Fermi)',
      revenant: 'Integra builds the campus and owns the build-side OEM choice. Revenant supplies gas + midstream and is a candidate partner for on-site gas processing or compressor co-location, simplifying the lateral.',
      verdict: 'parity',
    },
    {
      factor: 'Fiber / connectivity',
      precedent: 'Lancium on backbone; Permian backbone (Pacifico); Amarillo metro (Fermi)',
      revenant: '3 BGP-anchored ILEC customer ports inside the AOI today; Houston Greenspoint rendezvous 114 mi south. Revenant\'s existing takeaway-pipeline relationships (KMI, ET) could simplify pipeline-ROW IRU negotiations for fiber laterals.',
      verdict: 'parity',
    },
    {
      factor: 'Substations within reach',
      precedent: '200 MW Lancium-funded for grid import; Pacifico fully off-grid; Fermi not central',
      revenant: `${subsCount} substations within 10-mi AOI buffer (available for the import scenario; not load-bearing since Revenant\'s gas supplies Integra\'s behind-meter generation directly).`,
      verdict: 'parity',
    },
  ];

  const IMPLICATION_ITEMS: Array<{ title: string; body: string }> = [
    {
      title: 'Permitting playbook is being publicly written inside the AOI.',
      body: 'Amp Z\'s $1.05B / 2.1 GW Project Lufkin in unincorporated Angelina County is the active local stress-test of TX SB 6 (75 MW+ ERCOT large-load rules), PUCT draft Rule 25.194 (BTM co-location gating), and TCEQ permitting. Polk County (one south) passed an oversight resolution citing Angelina; City of Lufkin issued a public FAQ; Texas counties lack zoning authority by statute. Integra inherits the playbook the moment it\'s written, and a proactive county-engagement template is the community-comms workstream that comes with it.',
    },
    {
      title: 'Window before public announcement closes pricing leverage.',
      body: 'A family next to Project Lufkin rejected a $26M offer for ~900 ac (~$28.9k/ac, KTRE Mar 2026) after the Amp Z announcement re-priced their land DC-adjacent. Raw East TX timberland today still trades at commodity comps a fraction of that. The window to lock institutional timber holders in the AOI at pre-announcement pricing closes the moment Integra (or any other developer) goes public.',
    },
    {
      title: 'Land options exceed precedent depth.',
      body: `Each precedent had to fit a campus on 1,041–8,000 ac of single-owner land. The AOI offers ${a2k} single-owner assemblies of 2,000+ ac (${a5k} of those at 5,000+, ${a10k} at 10,000+). Optionality across five institutional holders means no specific block has to deliver.`,
    },
    {
      title: 'Corridor density gives Integra multiple FT counterparties.',
      body: `${ngpl ? `NGPL Gulf Coast ${ngpl.diameter_in || ''}″ is one of the named corridor lines; ` : ''}${takeawayLines} takeaway lines cross the AOI. If Revenant's ramp slips, Integra can evaluate multiple counterparties, subject to operator validation of scheduled quantity, receipt/delivery path, and firm transportation.`,
    },
    {
      title: 'Real risks are TCEQ + tenant; same gates as every BTM precedent.',
      body: 'East TX attainment makes the permit path cleaner than Meta El Paso\'s, and Project Lufkin\'s active TCEQ engagement provides a working local template. The tenant question simplifies to "do we sign Integra"; Fermi shows what failing this question looks like.',
    },
    {
      title: 'Counterparty depth cuts both ways.',
      body: 'Five institutional timber holders concentrate the eligible parcels across the AOI: enough that no single counterparty can obstruct, and the largest blocks carry pricing leverage. Surface deal structure: purchase preferred; long-term lease with surface-use carve-outs as fallback.',
    },
  ];

  return (
    <section style={section}>
      <details open>
        <summary style={{ cursor: 'pointer', listStyle: 'none', outline: 'none', marginBottom: 8 }}>
          <span style={{ ...kicker, marginRight: 10 }}>Appendix A</span>
          <span style={{ ...sectionH2, display: 'inline-block', margin: 0 }}>Precedent realism check</span>
        </summary>
        <p style={sectionLede}>
          The 6.1 GW-e headline assumes a footprint comparable to publicly-disclosed Texas BTM builds. The most directly load-bearing precedent is happening <b>inside this AOI right now</b> ({heroPrecedent.name}); the side-by-side below stress-tests the claim against three additional Texas patterns (<b>Crusoe @ Lancium</b>, <b>Pacifico GW Ranch</b>, <b>Fermi Matador</b>). Sources at the bottom.
        </p>

        <div style={{
          marginTop: 14, marginBottom: 18,
          padding: 18,
          background: REPORT.amberWash,
          border: `1px solid ${REPORT.accent}`,
          borderTop: `4px solid ${REPORT.accent}`,
          borderRadius: 4,
          fontFamily: SANS,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: REPORT.accent,
            letterSpacing: 1.5, textTransform: 'uppercase',
          }}>
            Inside this AOI · The closest analog
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: REPORT.heading, fontFamily: SERIF }}>{heroPrecedent.name}</div>
            <div style={{ fontSize: 12, color: REPORT.secondary, marginTop: 2 }}>{heroPrecedent.sub}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18, paddingTop: 6 }}>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: 1 }}>Capacity</div>
              <div style={{ fontSize: 13, color: REPORT.heading, fontWeight: 600, marginTop: 3 }}>{heroPrecedent.capacity}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: 1 }}>Land</div>
              <div style={{ fontSize: 13, color: REPORT.heading, fontWeight: 600, marginTop: 3 }}>{heroPrecedent.acres}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: REPORT.tertiary, textTransform: 'uppercase', letterSpacing: 1 }}>Density</div>
              <div style={{ fontSize: 13, color: REPORT.heading, fontWeight: 600, marginTop: 3 }}>{heroPrecedent.acPerGw}</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: REPORT.body, lineHeight: 1.55 }}>{heroPrecedent.body}</div>
        </div>

        <h3 style={{ ...sectionH2, fontSize: 15, marginTop: 18 }}>Side-by-side</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: SANS }}>
            <thead>
              <tr style={{ background: REPORT.surface }}>
                {['Project', 'Capacity', 'Acres', 'ac/GW', 'Gas / power source', 'Air permit', 'Land structure'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: REPORT.heading, borderBottom: `1px solid ${REPORT.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((r) => (
                <tr key={r.label} style={{ background: r.emphasis ? REPORT.amberWash : (r.inAoi ? REPORT.surface : 'transparent') }}>
                  <td style={{ padding: '8px 10px', color: REPORT.heading, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top', minWidth: 180 }}>
                    <div style={{ fontWeight: (r.emphasis || r.inAoi) ? 700 : 500 }}>{r.label}</div>
                    {r.sub && <div style={{ fontSize: 10, color: REPORT.tertiary, marginTop: 2 }}>{r.sub}</div>}
                    {r.context && <div style={{ fontSize: 11, color: REPORT.secondary, fontStyle: 'italic', marginTop: 4, lineHeight: 1.4 }}>{r.context}</div>}
                  </td>
                  <td style={{ padding: '8px 10px', color: REPORT.body, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.mw}</td>
                  <td style={{ padding: '8px 10px', color: REPORT.body, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.acres}</td>
                  <td style={{ padding: '8px 10px', color: REPORT.heading, fontWeight: 600, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.acPerGw}</td>
                  <td style={{ padding: '8px 10px', color: REPORT.body, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.gas}</td>
                  <td style={{ padding: '8px 10px', color: REPORT.body, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.permit}</td>
                  <td style={{ padding: '8px 10px', color: REPORT.body, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.land}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ ...sectionLede, marginTop: 10, marginBottom: 0 }}>
          <b>Headline takeaway.</b> Each precedent had to fit its campus on a single block of 1,100–8,000 ac. The AOI offers <b>{a2k} single-owner assemblies of 2,000+ ac</b> ({a5k} of those at 5,000+ ac, {a10k} at 10,000+ ac). Land options exceed precedent depth; realism risks live elsewhere, see audit below.
        </p>

        <h3 style={{ ...sectionH2, fontSize: 15, marginTop: 24 }}>Internal audit: same enablers as the precedents?</h3>
        <p style={sectionLede}>For each success factor that drove the precedents, what Revenant brings as Integra's gas + midstream partner and what the AOI carries today.</p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: SANS }}>
            <thead>
              <tr style={{ background: REPORT.surface }}>
                {['Success factor', 'Precedent', 'Revenant East TX (audit)', 'Verdict'].map((h) => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: REPORT.heading, borderBottom: `1px solid ${REPORT.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {AUDIT_ROWS.map((r) => {
                const v = VERDICT_META[r.verdict];
                return (
                  <tr key={r.factor}>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: REPORT.heading, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.factor}</td>
                    <td style={{ padding: '8px 10px', color: REPORT.body, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.precedent}</td>
                    <td style={{ padding: '8px 10px', color: REPORT.body, borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>{r.revenant}</td>
                    <td style={{ padding: '8px 10px', borderBottom: `1px solid ${REPORT.border}`, verticalAlign: 'top' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 3,
                        background: v.bg, color: v.fg, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase',
                      }}>{v.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <h3 style={{ ...sectionH2, fontSize: 15, marginTop: 24 }}>Implications for IC</h3>
        <ol style={{ margin: '8px 0 0 18px', padding: 0, fontFamily: SANS, fontSize: 13, color: REPORT.body, lineHeight: 1.55 }}>
          {IMPLICATION_ITEMS.map((it) => (
            <li key={it.title} style={{ marginBottom: 10 }}>
              <b style={{ color: REPORT.heading }}>{it.title}</b> {it.body}
            </li>
          ))}
        </ol>

        <details style={{ marginTop: 16 }}>
          <summary style={{
            cursor: 'pointer', fontSize: 11, fontWeight: 600, color: REPORT.secondary,
            fontFamily: SANS, padding: '6px 0',
          }}>
            Methodology and sources
          </summary>
          <div style={{
            marginTop: 8, padding: 12,
            background: REPORT.surface, border: `1px solid ${REPORT.border}`, borderRadius: 4,
            fontSize: 11, color: REPORT.secondary, fontFamily: SANS, lineHeight: 1.55,
          }}>
            <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Crusoe @ Lancium (Taylor Co)</div>
            <ul style={{ margin: '0 0 10px 0', paddingLeft: 18 }}>
              <li><a href="https://lancium.com/2025/03/18/crusoe-expands-ai-data-center-campus-in-abilene-to-1-2-gigawatts/" target="_blank" rel="noreferrer" style={{ color: REPORT.accent }}>Lancium, Crusoe expands to 1.2 GW (Mar 2025)</a></li>
              <li><a href="https://www.datacenterfrontier.com/hyperscale/article/55276169/crusoe-adds-45-gw-natural-gas-to-fuel-ai-expands-abilene-data-center-to-12-gw" target="_blank" rel="noreferrer" style={{ color: REPORT.accent }}>Data Center Frontier, Crusoe adds 4.5 GW gas</a></li>
            </ul>

            <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Pacifico GW Ranch (Pecos Co)</div>
            <ul style={{ margin: '0 0 10px 0', paddingLeft: 18 }}>
              <li><a href="https://www.businesswire.com/news/home/20260126236053/en/Pacifico-Energy-Secures-7.65-GW-Power-Generation-Permit-for-GW-Ranch-Project" target="_blank" rel="noreferrer" style={{ color: REPORT.accent }}>BusinessWire, Pacifico 7.65 GW permit (Jan 2026)</a></li>
              <li><a href="https://www.datacenterdynamics.com/en/news/pacifico-energy-unveils-5gw-offgrid-natural-gas-project-in-texas-slated-to-serve-a-hyperscale-data-center/" target="_blank" rel="noreferrer" style={{ color: REPORT.accent }}>DCD, Pacifico 5 GW off-grid project</a></li>
            </ul>

            <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Fermi Matador (Carson Co)</div>
            <ul style={{ margin: '0 0 10px 0', paddingLeft: 18 }}>
              <li><a href="https://fermiamerica.com/fermi-americatm-and-the-state-of-texas-announce-preliminary-approval-for-first-6-gw-of-one-of-the-worlds-largest-clean-natural-gas-facilities-on-project-matadors-11-gw-private-hypergridtm-campus/" target="_blank" rel="noreferrer" style={{ color: REPORT.accent }}>Fermi America, 6 GW preliminary approval</a></li>
              <li><a href="https://www.hartenergy.com/policy-and-regulation/permitting/he-fermi-america-hypergrid-1142025/" target="_blank" rel="noreferrer" style={{ color: REPORT.accent }}>Hart Energy, Fermi 6 GW gas plant approved</a></li>
              <li><a href="https://www.datacenterdynamics.com/en/news/fermi-share-price-craters-as-tenant-pulls-back-from-150m-texas-data-center-deal/" target="_blank" rel="noreferrer" style={{ color: REPORT.accent }}>DCD, Fermi tenant pulls $150M deal</a></li>
            </ul>

            <div style={{ fontWeight: 600, color: REPORT.heading, marginBottom: 4 }}>Amp Z Project Lufkin (in-AOI, Angelina Co)</div>
            <div>
              Lufkin Daily News (Feb &amp; Apr 2026); Data Center Dynamics (Feb &amp; Mar 2026); BaxTel; KLTV / KTRE / KETK; City of Lufkin FAQ; East Texas Banner (Apr 2026); GlobeNewswire / GridAI Technologies (Mar 2026); OTC Markets / CBDW filings; Texas Tribune data-center series (Apr 2026).
            </div>
          </div>
        </details>
      </details>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Style atoms
// ─────────────────────────────────────────────────────────────────────────────

const pageBase: React.CSSProperties = {
  background: REPORT.surface,
  color: REPORT.body,
  fontFamily: SANS,
  minHeight: '100vh',
};

const stickyWrapper: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: REPORT.surface,
  paddingTop: 10,
  paddingBottom: 10,
  zIndex: 2,
};

const toolbar: React.CSSProperties = {
  padding: '10px 18px',
  background: REPORT.heading,
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  gap: 18,
  maxWidth: 1280,
  margin: '0 auto',
  borderRadius: 4,
  boxSizing: 'border-box',
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
};

const btn: React.CSSProperties = {
  marginLeft: 8,
  padding: '4px 10px',
  background: 'transparent',
  border: '1px solid rgba(212,152,128,0.4)',
  color: REPORT.patina,
  borderRadius: 3,
  cursor: 'pointer',
  fontSize: 12,
  fontFamily: SANS,
};

const mastheadCross: React.CSSProperties = {
  fontFamily: SERIF,
  fontSize: 16,
  fontStyle: 'italic',
  color: REPORT.patina,
  opacity: 0.75,
  lineHeight: 1,
};

const mastheadMeta: React.CSSProperties = {
  fontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
  fontSize: 9.5,
  fontWeight: 500,
  letterSpacing: '0.10em',
  textTransform: 'uppercase',
  color: REPORT.patina,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  borderLeft: '1px solid rgba(212,152,128,0.28)',
  paddingLeft: 14,
};

const section: React.CSSProperties = {
  padding: '24px 32px',
  borderBottom: `1px solid ${REPORT.border}`,
  background: REPORT.white,
  maxWidth: 1280,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const kicker: React.CSSProperties = {
  fontSize: 10, color: REPORT.accent, textTransform: 'uppercase',
  letterSpacing: 2, fontWeight: 700, fontFamily: SANS,
};

const coverH1: React.CSSProperties = {
  margin: '8px 0 4px',
  fontSize: 32,
  fontFamily: SERIF,
  fontWeight: 600,
  color: REPORT.heading,
  letterSpacing: -0.3,
};

const coverSub: React.CSSProperties = {
  fontSize: 13,
  color: REPORT.secondary,
  fontFamily: SANS,
};

const pitchLead: React.CSSProperties = {
  margin: '20px 0 0',
  fontSize: 16,
  lineHeight: 1.65,
  color: REPORT.body,
  fontFamily: SERIF,
};

const sectionH2: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 20,
  fontWeight: 600,
  color: REPORT.heading,
  fontFamily: SERIF,
  letterSpacing: -0.2,
};

const sectionLede: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: 13,
  color: REPORT.body,
  lineHeight: 1.55,
  fontFamily: SANS,
};

function Cell({ big, sub, emphasis }: { big: string; sub: string; emphasis?: boolean }) {
  return (
    <div>
      <div style={{
        fontSize: 22, fontWeight: 600,
        color: emphasis ? REPORT.accent : REPORT.heading,
        fontFamily: SERIF, letterSpacing: -0.3,
      }}>
        {big}
      </div>
      <div style={{ fontSize: 11, color: REPORT.secondary, fontFamily: SANS, marginTop: 2, lineHeight: 1.3 }}>
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Share-link create modal: owner-only flow. POSTs to /admin/zone-share-tokens
// (requires owner role server-side; viewers / clients see 403).
// ─────────────────────────────────────────────────────────────────────────────
function ShareLinkModal({
  slug, regionLabel, state, onChange, onClose,
}: {
  slug: string;
  regionLabel: string;
  state: Extract<ShareModalState, { open: true }>;
  onChange: (s: ShareModalState) => void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(180);
  const [copied, setCopied] = useState(false);
  const created = state.created;

  const submit = async () => {
    onChange({ open: true, submitting: true, error: null });
    try {
      const trimmed = label.trim();
      const result = await createZoneShareToken(slug, trimmed || undefined, days);
      onChange({ open: true, created: result });
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const msg = e?.response?.data?.error || e?.message || 'Failed to create share link';
      const status = e?.response?.status;
      onChange({ open: true, error: status === 401 || status === 403 ? 'Owner role required to create share links.' : msg });
    }
  };

  const copy = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // older browsers; let the user select manually
    }
  };

  return (
    <div style={modalScrim} onClick={onClose}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 11, letterSpacing: 1.5, color: REPORT.tertiary, marginBottom: 6, textTransform: 'uppercase' }}>
          Share link
        </div>
        <h2 style={{ fontFamily: SERIF, fontSize: 20, margin: 0, color: REPORT.heading, marginBottom: 4 }}>
          {regionLabel}
        </h2>
        <div style={{ fontSize: 12, color: REPORT.secondary, fontFamily: SANS, marginBottom: 18 }}>
          Tracked, recipient-labelled, revocable. Live view of the dashboard.
        </div>

        {!created && (
          <>
            <label style={modalLabel}>
              Recipient label
              <input
                type="text"
                placeholder='e.g. "Integra IC: Apr 2026"'
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                maxLength={100}
                style={modalInput}
              />
            </label>
            <label style={modalLabel}>
              Expires in
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={days}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v)) setDays(Math.min(365, Math.max(1, Math.floor(v))));
                  }}
                  style={{ ...modalInput, width: 80, marginTop: 0 }}
                />
                <span style={{ color: REPORT.secondary, fontSize: 12 }}>days (1-365, default 180)</span>
              </div>
            </label>
            {state.error && (
              <div style={{ color: REPORT.fail, fontSize: 12, marginTop: 8, fontFamily: SANS }}>{state.error}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
              <button style={btnSecondary} onClick={onClose} disabled={state.submitting}>Cancel</button>
              <button style={btnPrimary} onClick={submit} disabled={state.submitting}>
                {state.submitting ? 'Creating…' : 'Create link'}
              </button>
            </div>
          </>
        )}

        {created && (
          <>
            <div style={{ fontSize: 12, color: REPORT.body, fontFamily: SANS, marginBottom: 8 }}>
              {created.label ? <>Recipient: <b>{created.label}</b></> : <em style={{ color: REPORT.tertiary }}>No recipient label</em>}
              <br />
              Expires: <b>{new Date(created.expires_at).toLocaleDateString()}</b>
            </div>
            <div style={modalUrlBox}>
              <code style={{ fontSize: 12, fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-all' }}>
                {created.url}
              </code>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={btnSecondary} onClick={copy}>{copied ? 'Copied' : 'Copy URL'}</button>
              <button style={btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const modalScrim: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(20,28,40,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};
const modalCard: React.CSSProperties = {
  background: '#fff', padding: 28, borderRadius: 6,
  width: 460, maxWidth: '90vw', boxShadow: '0 18px 40px rgba(0,0,0,0.25)',
};
const modalLabel: React.CSSProperties = {
  display: 'block', fontSize: 11, color: REPORT.heading,
  fontFamily: SANS, fontWeight: 600, letterSpacing: 0.3,
  textTransform: 'uppercase', marginTop: 14,
};
const modalInput: React.CSSProperties = {
  width: '100%', padding: '8px 10px', marginTop: 4,
  border: `1px solid ${REPORT.border}`, borderRadius: 3,
  fontSize: 13, fontFamily: SANS, color: REPORT.body, background: '#fff',
  boxSizing: 'border-box',
};
const modalUrlBox: React.CSSProperties = {
  background: REPORT.surface, padding: 12, borderRadius: 3,
  border: `1px solid ${REPORT.border}`, marginTop: 8,
};
const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', fontSize: 12, fontWeight: 600, fontFamily: SANS,
  background: REPORT.heading, color: '#fff',
  border: `1px solid ${REPORT.heading}`, borderRadius: 3, cursor: 'pointer',
  letterSpacing: 0.4,
};
const btnSecondary: React.CSSProperties = {
  padding: '8px 16px', fontSize: 12, fontFamily: SANS,
  background: '#fff', color: REPORT.body,
  border: `1px solid ${REPORT.border}`, borderRadius: 3, cursor: 'pointer',
  letterSpacing: 0.4,
};
